<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import {
  api,
  type BacktestParams,
  type BacktestJob,
  type BacktestConfigStore,
  type BacktestResultData,
  type WalkForwardResultData,
  type HedgeParams,
  type OverlayParams,
  type MetricSummary,
  type RankedResult,
} from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useFundEnablement } from '../composables/useFundEnablement'
import { usePortfolio } from '../composables/usePortfolio'
import { backtestAssetId, backtestConfigFund, FUND_REGISTRY, type PortfolioSlug, type BacktestConfigFund } from './nav/fundConfig'
import OptimizerResultsTable from './OptimizerResultsTable.vue'
import ParamHeatmap from './ParamHeatmap.vue'
import WalkForwardChart from './WalkForwardChart.vue'
import CpcvPathsChart from './CpcvPathsChart.vue'

const { isSuperUser, isAuthenticated, loading: authLoading, user } = useAuth()
const { portfolio } = usePortfolio()
// portfolioId is the plain string value; in tests the mock may return a
// plain-object ref-alike so we read .value defensively.
const portfolioId = computed<string>(() =>
  typeof (portfolio as any).value === 'string' ? (portfolio as any).value : (portfolio as any)
)
// Task 10: job routes use the engine asset id, config routes the fund slug (japan ≠ lg_japan_index).
const btAsset = computed(() => backtestAssetId(portfolioId.value as PortfolioSlug))
const btFund = computed(() => backtestConfigFund(portfolioId.value as PortfolioSlug))
const registryEntry = computed(() => FUND_REGISTRY[portfolioId.value as PortfolioSlug])
// Standalone sign-rule-only backtest (japan, silver): no auto-optimise, pure step-aside, no schedule.
const isSignRuleOnly = computed(() => registryEntry.value?.backtestSignRuleOnly === true)
// Copy for the sign-rule funds, by Fund discriminator. Until 2026-10-11 every string on this path said
// Japan / NIY / "JAPAN grant" whichever fund was loaded (Paolo: "it can't be Japan and NYI, this is Silver
// and SI futures"). A new sign-rule fund gets a row here; the fallback uses the registry label.
interface SignRuleCopy { fund: string; futures: string; grant: string; rule: string; hint: string }
const SIGN_RULE_COPY: Record<string, SignRuleCopy> = {
  JAPAN: {
    fund: 'Japan', futures: 'NIY', grant: 'JAPAN',
    rule: 'L&G Japan 08:40 sign rule — NIY futures from the Tokyo close to 08:40 UK (step-aside pure)',
    hint: 'Japan: "futures" = CME NIY since the last Tokyo cash close.',
  },
  CHARTERIS: {
    fund: 'Silver', futures: 'SI', grant: 'CHARTERIS',
    rule: 'Silver 08:40 sign rule — COMEX SI futures from the previous 16:00 New York close to 08:40 UK (step-aside pure)',
    hint: 'Silver: "futures" = COMEX SI since the previous 16:00 New York close.',
  },
  AMATI: {
    fund: 'Copper', futures: 'HG', grant: 'AMATI',
    rule: 'Copper 08:40 sign rule — COMEX HG futures from the previous 16:00 New York close to 08:40 UK (step-aside pure)',
    hint: 'Copper: "futures" = COMEX HG since the previous 16:00 New York close.',
  },
}
const signRuleCopy = computed<SignRuleCopy>(() => {
  const e = registryEntry.value
  return SIGN_RULE_COPY[e?.fund ?? ''] ?? {
    fund: e?.label ?? 'this fund', futures: 'futures', grant: e?.fund ?? 'fund',
    rule: `${e?.label ?? 'Fund'} 08:40 sign rule (step-aside pure)`,
    hint: `${e?.label ?? 'Fund'}: "futures" = the fund's reference futures since the previous session close.`,
  }
})
// Task 10 fix round 1: running a gated fund's backtest needs the grant (server 404s otherwise). A
// superuser previewing without it sees the panel read-only: no Run, no mount auto-run.
// Final review 2026-09-30 item 2: the grant comes from useFundEnablement().entitledFunds — the
// fail-closed per-user /me source every other gated-fund UI uses (it follows view-as switches) —
// never useAuth().user.entitledFunds, which /login and /login/verify leave null until a reload.
const { entitledFunds } = useFundEnablement()
// Running a gated fund's backtest needs the grant (server 404s otherwise); a superuser previewing
// without it sees the panel read-only. Source: useFundEnablement().entitledFunds (fail-closed /me).
const gatedGranted = computed(() =>
  !registryEntry.value?.requiresEntitlement || entitledFunds.value?.includes(registryEntry.value.fund) === true)
const gatedRunBlocked = computed(() => !gatedGranted.value)

// ---- Reactive state ----

const defaultParams: BacktestParams = {
  zWindow: 120,
  tiltBps: 20,
  vixScale: 1.0,
  termStructure: true,
  nfpFilter: false,
  holidayFilter: true,
  opexFilter: false,
  quadWitchingFilter: false,
  exDividendFilter: false,
  futuresRollFilter: true,
  eomInterestFilter: false,
  eomInterestBoostBps: 29,
  noHold: true,
  imrMode: 'momentum',
  imrTiltBps: 0,
  imrWindow: 10,
  imrBreadthThreshold: 0.5,
  vixMomGate: 'off',
  autoOptimize: true,
  useHedging: false,
  useOverlay: false,
  useShortConviction: false,
  shortConviction: undefined,
  useLongConviction: false,
  longConviction: undefined,
  otpReservePct: 0.33,
  // Deprecated (2026-07-16, recalibration bias decommissioned) — no UI
  // control below, but kept + defaulted so a full-file preset save still
  // round-trips these fields for the C# backtest engine.
  applyRecalibrationBias: false,
  recalibrationWindowDays: 10,
  // Mirrors the C# BacktestParams default. Only takes effect when the preset
  // also has useCanonicalSynthNav (not surfaced here; round-trips via spread).
  synthNav945Method: 'aggregate',
  // Phase 3 of project_cusum_event_time_backtest_plan.md.
  // "daily" (default) = legacy per-trading-day iteration.
  // "event"           = iterate only on CUSUM-event dates (López ch. 2).
  // Default left at "daily" so existing presets round-trip identically.
  sampling: 'daily',
  eventSamplingNavSource: 'official',
  // NQ-overnight step-aside gate (JH probe 2026-07-23). Default off so
  // existing presets round-trip identically.
  stepAsideMode: 'off',
  stepAsideThresholdBps: 0,
  // 2026-09-15 nullable: null = legacy one-sided rule (no dead zone).
  // A non-null value opts into the dead zone and MUST be >= the sell
  // threshold; guard fires client-side (stepAsideBandError) and the API
  // refuses too.
  stepAsideBuyThresholdBps: null as number | null,
  stepAsideReentryBps: null,
  // Post-Databento window clamp. Off for BGA/JH: BGA has no step-aside
  // series (no-op) and JH's full window is the deliberate baseline. The
  // field must still exist here so the save/apply diff guards compare it
  // symmetrically once the server echoes it on job.parameters. Gold's
  // panel defaults it ON.
  postDatabentoOnly: false,
  imrMomGateDeadZone: 0,
  newsSentimentFilter: false,
  newsSentimentThreshold: 0.15,
  newsSentimentMinCoveragePct: 30,
  newsSentimentMode: 'buy_only',
  newsSentimentUseZ: false,
  newsSentimentZThreshold: -1.0,
}
const params = ref<BacktestParams>({ ...defaultParams })
const validationMode = ref<'none' | 'walk-forward' | 'cpcv'>('walk-forward')
const walkForwardEnabled = computed(() => validationMode.value === 'walk-forward')
// 2026-09-15 step-aside dead-zone guard (paolo Q2). Mirrors
// BacktestParams.ValidateStepAsideBand() so the operator sees the error
// before hitting Submit and burning a request.
const stepAsideBandError = computed<string | null>(() => {
  const sell = params.value.stepAsideThresholdBps ?? 0
  const buy = params.value.stepAsideBuyThresholdBps
  // Null = legacy one-sided rule (no dead zone) — always valid.
  if (buy != null && buy < sell)
    return `Futures rise to trigger BUY (${buy}bps) must be >= Futures drop to trigger SELL (${sell}bps) — otherwise the dead-zone rule flips into an unstable oscillator around the crossover.`
  return null
})
// 2026-09-15: default WF submits type "walk-forward-quick" (24-combo grid); the
// "Full grid" checkbox opts into the exhaustive 240-combo run — that one takes
// ~5-25h on gold's post-Databento window and produced the 42-fold runaway we
// caught yesterday. See docs/analysis/2026-09-15-walk-forward-runaway-fix.md.
const wfFullGrid = ref(false)
const trainDays = ref(250)
const testDays = ref(60)
const fitnessPreset = ref<'sharpe_only' | 'balanced' | 'risk_adjusted'>('sharpe_only')
const calendarExpanded = ref(false)
const cpcvParamsExpanded = ref(true) // default expanded when CPCV radio selected
const advancedRunsExpanded = ref(false)

const activeJob = ref<BacktestJob | null>(null)
const activeJobId = ref<string | null>(null)
const configs = ref<BacktestConfigStore | null>(null)
const selectedConfig = ref('')
// System-locked presets (test fixtures like gold_mt_y10_v1, warm_start) are
// hidden from the dropdown and their Delete button is disabled. See
// BacktestConfigService.PresetLockedException / paolo 2026-09-19.
const visibleConfigNames = computed(() => {
  const all = configs.value?.configs ?? {}
  return Object.entries(all)
    .filter(([name, e]) => !e.system_locked || name === selectedConfig.value)
    .map(([name]) => name)
})
const selectedIsLocked = computed<boolean>(() =>
  !!(selectedConfig.value && configs.value?.configs?.[selectedConfig.value]?.system_locked))
const cpcvN = ref(6)
const cpcvK = ref(2)
const cpcvEmbargoDays = ref(5)
const tpeTrialBudget = ref(1000)
const sweepOverlaySc = ref(false)
const overlaySweepBudget = ref('standard')
const controlsCollapsed = ref(false)
const completedJobs = ref<BacktestJob[]>([])

const loading = ref(false)
const error = ref<string | null>(null)
const showScheduleInput = ref(false)
const activeTooltip = ref<string | null>(null)

function toggleTooltip(id: string) {
  activeTooltip.value = activeTooltip.value === id ? null : id
}

const tooltips: Record<string, string> = {
  imrMode: 'Instrument-level mean reversion mode. "momentum" is the current production setting — uses IMR momentum instead of the traditional z-score signal.',
  vmg: 'VIX Momentum Gate — raises the wz threshold in high VIX or backwardation. "both" is current production. "circuit" adds a hard VIX>35 SELL trigger.',
  noHold: 'Eliminates the HOLD zone. With noHold=true, every day is BUY or SELL (no straddle). Current production: on.',
  otpReserve: 'Cash held back to fund OTP strategies (hedge, overlay, short/long conviction) inside this backtest simulation. BGA deploys the remaining (1 − reserve) share of NPV. Applies whenever ANY OTP strategy is enabled, not just hedging. In-simulation only — since the OTP/BGA divorce (Phase 1), this field is NOT synced with any live-system setting; live OTP sizing is residual-funded (post-cutoff leftover cash), not an operator-set reserve. "NO OTP" pins the reserve at zero — fully invested, never swept, even under auto-optimize. "Sweep (auto)" lets Focused Grid search the reserve axis (0/10/20/33/50%) and pick the best; the dropdown repopulates with the winner. Any percentage pins that value across the grid. Full Optimize (TPE) also sweeps this dimension via the TpeSearchSpace.',
  fitnessPreset: '"dsr_aware" penalises multiple-testing bias. "balanced" weights 8 metrics equally. "sharpe_only" ranks by Sharpe alone.',
  synthMethod: 'Which canonical 09:45 synth-NAV column the strategy consumes (when the preset uses the canonical source): "aggregate" is the live writer\'s primary row; "etf_trick" is the K_t-derived Method B column. Presets tuned on one method mis-measure if run on the other — databento_kt_fva_tuned_v1 was TPE-tuned on etf_trick but silently reverted to aggregate when a save dropped the field (2026-07-14). This control makes the value visible so that cannot recur.',
  walkForward: 'Walk-forward validation splits data into train/test folds, re-optimising on each train window and reporting out-of-sample test performance.',
  cpcv: 'Combinatorial Purged Cross-Validation — runs the strategy once on full data, then reports metrics on N non-overlapping windows. Gives a distribution of per-window performance.',
  sampling: 'Backtest sampling mode (López ch. 2). "daily" (default) iterates one decision per trading day (~252/yr) — every existing preset was tuned in this mode. "event" iterates only on CUSUM-event dates on BGA NAV (~48/yr at default params), giving honest Sharpe / DSR that aren\'t inflated by low-information drift days. Use event-mode for an honest measurement reading and daily-mode for production preset tuning until validation in Phase 4 establishes the gap.',
  eventNavSource: 'Which NAV series drives CUSUM event detection in event-sampling mode. "official" = published BGA NAV (legacy). "kt_synth" = canonical 09:45 K_t synthetic NAV (Databento). "fva_adjusted" = FV-anchored inferred BG NAV. Part of the 2026-07 CUSUM reassessment ablation.',
  stepAside: 'What drives the daily BUY/SELL decision. "Synth-NAV strategy" = the K_t synthetic-NAV mean-reversion signal with its gates; the futures rule is not used. "Hold unless futures down" = the futures rule only: stay in the fund every day, sell to cash when the 08:40 UK NAS100 print is below the prior close by more than the SELL bar; the synth NAV, FVA and every filter are bypassed. "Synth-NAV strategy, futures can force SELL" = the synth-NAV strategy decides as normal, but a futures print below the bar overrides it to SELL. (Config key: step_aside_mode off / pure / gate.)',
}

const cronExpression = ref('0 6 * * 1-5')
const saveConfigName = ref('')
const showSaveInput = ref(false)
const showResultSave = ref(false)
const showRenameInput = ref(false)
const renameConfigName = ref('')

function defaultHedgeParams(): HedgeParams {
  return {
    triggerType: 'percentage',
    triggerThreshold: 5,
    hedgeSizeRatio: 1.0,
    spreadBps: 20,
    annualTer: 0.008,
    leverageFactor: -3.0,
    bgaBeta: 0.85,
    cashBufferPct: 0.33,
    dealingChargeGbp: 6.95,
  }
}

watch(() => params.value.useHedging, (enabled) => {
  if (enabled && !params.value.hedge) {
    params.value.hedge = defaultHedgeParams()
  } else if (!enabled) {
    params.value.hedge = undefined
  }
})

function defaultOverlayParams(): OverlayParams {
  return {
    triggerThreshold: -2.0,
    overlaySizePct: 0.33,
    spreadBps: 20,
    annualTer: 0.0075,
    leverageFactor: 3.0,
    dealingChargeGbp: 6.95,
  }
}

watch(() => params.value.useOverlay, (enabled) => {
  if (enabled && !params.value.overlay) {
    params.value.overlay = defaultOverlayParams()
  } else if (!enabled) {
    params.value.overlay = undefined
  }
})

watch(() => params.value.useShortConviction, (val) => {
  if (val && !params.value.shortConviction) {
    params.value.shortConviction = {
      triggerType: 'percentage',
      triggerThreshold: 3,
      hedgeSizeRatio: 1.0,
      spreadBps: 20,
      annualTer: 0.008,
      leverageFactor: -3.0,
      bgaBeta: 0.85,
      cashBufferPct: 0.15,
      dealingChargeGbp: 6.95,
    }
  }
})

watch(() => params.value.useLongConviction, (val) => {
  if (val && !params.value.longConviction) {
    params.value.longConviction = {
      triggerThreshold: -2.0,
      overlaySizePct: 0.25,
      spreadBps: 18,
      annualTer: 0.0075,
      leverageFactor: 3.0,
      dealingChargeGbp: 6.95,
    }
  }
})

let pollTimer: ReturnType<typeof setInterval> | null = null

/** A grid row loaded via its Load button: its params are in the form and its full-period result
 *  drives the KPIs/graph. Scoped to the job it came from — a different job clears it. */
const loadedRow = ref<{ jobId: string; rank: number; params: BacktestParams; result: BacktestResultData } | null>(null)
watch(() => activeJob.value?.jobId, (id) => {
  if (loadedRow.value && loadedRow.value.jobId !== id) loadedRow.value = null
})

// ---- Computed properties ----

const currentResult = computed<BacktestResultData | null | undefined>(() => {
  const job = activeJob.value
  if (!job) return null
  // 2026-10-07 (Paolo): Load on a grid row shows THAT row's full-period result in the KPI
  // cards and equity curve, while the job (and its grid table / heatmap) stays on screen.
  const row = loadedRow.value
  if (row && row.jobId === job.jobId && row.result?.equityCurve?.length) return row.result
  const wf = job.walkForwardResult
  // Pick the first result with actual data — refitResult can exist but be all zeros
  // (optimize-wf-focused doesn't do per-fold refitting, leaving refitResult empty)
  const candidates: Array<BacktestResultData | null | undefined> = [
    wf?.refitResult,
    wf?.fixedResult,
    job.result,
    wf?.inSampleResult,
  ]
  return candidates.find(r => r && r.equityCurve && r.equityCurve.length > 0) ?? null
})

const hasWalkForward = computed(() => !!activeJob.value?.walkForwardResult)

const optimizeResult = computed(() => activeJob.value?.optimizeResult ?? null)
const wfOptimizeResult = computed(() => activeJob.value?.walkForwardResult ?? null)
const tpeResult = computed(() => activeJob.value?.tpeResult ?? null)

const runTypeLabel = computed(() => (activeJob.value ? jobTypeLabel(activeJob.value.type) : ''))

function jobTypeLabel(t: string): string {
  if (t === 'cpcv') return 'CPCV'
  if (t === 'full-optimize') return 'Full Optimize (TPE)'
  if (t === 'optimize-wf-overlay') return 'Overlay/SC Sweep'
  if (t === 'optimize-wf-focused') return 'Optimize-WF-Focused'
  if (t === 'optimize-wf') return 'Optimize-WF'
  if (t === 'optimize') return 'Optimize'
  if (t === 'walk-forward') return 'Walk-Forward'
  return 'Single Backtest'
}

// 2026-10-07 (Paolo): a finished run (e.g. an overnight focused grid) could not be reopened once
// the page loaded the active preset's linked job. The picker lists this fund's completed runs
// (the API keeps them in memory until it restarts) and loads the chosen one in full.
const pickedRunId = ref('')
const recentRuns = computed(() =>
  [...completedJobs.value]
    .sort((a, b) => (b.completedAt ?? b.createdAt).localeCompare(a.completedAt ?? a.createdAt))
    .slice(0, 40))

function recentRunLabel(j: BacktestJob): string {
  const when = new Date(j.completedAt ?? j.createdAt)
  const stamp = isNaN(when.getTime()) ? '' : when.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  const sharpe = j.result?.sharpeRatio
  return `${jobTypeLabel(j.type)} · ${stamp}${sharpe != null ? ` · Sharpe ${sharpe.toFixed(2)}` : ''}`
}

async function openRecentRun(id: string) {
  if (!id) return
  error.value = null
  try {
    const job = await api.backtestJob(id)
    if (job) {
      activeJob.value = job
      activeJobId.value = job.jobId
    }
  } catch (e: any) {
    error.value = `Could not open run ${id}: ${e?.message ?? e}`
  } finally {
    pickedRunId.value = ''
  }
}

const runSummaryMeta = computed(() => {
  const job = activeJob.value
  if (!job) return ''
  const parts: string[] = []
  const grid = job.walkForwardResult?.fixedGridResult ?? job.optimizeResult
  if (grid?.totalCombos) parts.push(`${grid.totalCombos} combos`)
  if (job.walkForwardResult?.folds?.length) parts.push(`${job.walkForwardResult.folds.length} folds`)
  const elapsedMs = grid?.elapsedMs ?? job.cpcvResult?.elapsedMs ?? 0
  if (elapsedMs) parts.push(`${(elapsedMs / 1000).toFixed(0)}s`)
  if (job.completedAt) parts.push(new Date(job.completedAt).toISOString().slice(0, 16).replace('T', ' ') + ' UTC')
  return parts.join(' | ')
})

const runWinnerSummary = computed(() => {
  const wf = activeJob.value?.walkForwardResult
  if (!wf?.fixedParams) return ''
  const p = wf.fixedParams
  // Pure step-aside ("Hold unless futures down") decides on the futures print alone — the band is
  // the swept knob and z/tilt/vix are inert, so show the band (2026-10-07).
  if (p.stepAsideMode === 'pure') {
    return `SELL ${p.stepAsideThresholdBps} bp / BUY ${p.stepAsideBuyThresholdBps ?? p.stepAsideThresholdBps} bp`
  }
  if (p.imrMode === 'momentum') {
    return `imrWindow=${p.imrWindow}, imrBreadthThreshold=${p.imrBreadthThreshold}, vmg=${p.vixMomGate}`
  }
  return `z=${p.zWindow}, tilt=${p.tiltBps}, vix=${p.vixScale}, vmg=${p.vixMomGate}`
})

const winnerDsr = computed<number | null>(() => {
  const scores = activeJob.value?.walkForwardResult?.fixedGridResult?.allScores
    ?? activeJob.value?.optimizeResult?.allScores
    ?? []
  const first = scores[0]
  return first?.deflatedSharpe ?? null
})

function dsrColour(dsr: number): string {
  if (dsr > 2) return '#4caf50'
  if (dsr >= 1) return '#fbc02d'
  return '#f44336'
}

const primaryRunLabel = computed(() => {
  const v = validationMode.value
  const a = params.value.autoOptimize
  if (v === 'cpcv') return 'Validate with CPCV'
  if (v === 'walk-forward' && a)  return sweepOverlaySc.value ? 'Run Overlay/SC Sweep (WF)' : 'Run Focused Grid (DSR)'
  if (v === 'walk-forward' && !a) return 'Run Walk-Forward'
  if (v === 'none' && a)  return 'Run Full Grid (960)'
  return 'Run Single Backtest'
})

function binomial(n: number, k: number): number {
  if (k < 0 || k > n) return 0
  if (k === 0 || k === n) return 1
  let result = 1
  for (let i = 1; i <= k; i++) result = result * (n - i + 1) / i
  return Math.round(result)
}

const stackPreview = computed(() => {
  const p = params.value as any
  const bits = [
    p.synthNav945Method ?? 'aggregate',
    p.useCanonicalSynthNav ? 'canonical' : 'NON-CANONICAL',
    `FVA ${p.fvaSignalMode ?? 'off'}`,
  ]
  if (p.applyRecalibrationBias) bits.push('recal ON')
  return bits.join(' · ')
})
const stackWarning = computed(() => {
  const p = params.value as any
  return !p.useCanonicalSynthNav || (p.fvaSignalMode ?? 'off') === 'off' || !!p.applyRecalibrationBias
})

const runPreview = computed(() => {
  const v = validationMode.value
  const a = params.value.autoOptimize
  const momentum = params.value.imrMode === 'momentum'
  if (v === 'cpcv') {
    const paths = binomial(cpcvN.value, cpcvK.value)
    return `CPCV: N=${cpcvN.value}, k=${cpcvK.value} → ${paths} paths, embargo ${cpcvEmbargoDays.value}d`
  }
  if (v === 'walk-forward' && a) {
    // Sweep Overlay/SC reroutes the run to optimize-wf-overlay (momentum
    // grid → top-3 winners each re-swept over the full OTP trigger grid).
    // Combo counts are computed server-side from the panel's trigger
    // ranges, so no count is promised here — 2026-07-17: a 1,848-combo
    // sweep once launched under a "48 combos" label.
    if (sweepOverlaySc.value) {
      return `Overlay/SC Sweep: momentum grid → top-3 × OTP trigger grid (hours-scale) · ${fitnessPreset.value}`
    }
    // Pure step-aside ("Hold unless futures down") sweeps the SELL × BUY futures band — the synth-NAV
    // knobs are inert there (BacktestJobService.BuildHypothesisGrid, 2026-10-06).
    const pure = params.value.stepAsideMode === 'pure'
    const combos = pure ? 36 : momentum ? 48 : 36
    const sweep = pure
      ? 'Futures SELL × BUY band'
      : momentum
        ? 'IMR Window × Breadth × VMG'
        : 'Z Window × VIX Scale × VMG × Term Structure'
    return `Focused Grid: ${sweep} — ${combos} combos · ${fitnessPreset.value}`
  }
  if (v === 'walk-forward' && !a) {
    return `Walk-Forward: 1 combo × N folds · ${fitnessPreset.value}`
  }
  if (v === 'none' && a) {
    return 'Full Grid: 960 combos'
  }
  return 'Single Backtest: 1 run'
})

async function runPrimary() {
  // 2026-09-15 dead-zone guard runs at the entry point so it covers every
  // dispatch route (runBacktest, runOptimize, runOptimizeFocused, runCpcv).
  // Server refuses the same shape; this is the immediate client feedback.
  if (stepAsideBandError.value) {
    error.value = stepAsideBandError.value
    return
  }
  // Task 10 fix round 1: Japan always dispatches a plain single run — 2026-10-09: or CPCV, which the
  // server now accepts for sign-rule funds (sign rule applied before dispatch).
  if (isSignRuleOnly.value) return validationMode.value === 'cpcv' ? runCpcv() : runBacktest()
  const v = validationMode.value
  const a = params.value.autoOptimize
  if (v === 'cpcv') return runCpcv()
  if (v === 'walk-forward' && a)  return runOptimizeFocused()
  if (v === 'walk-forward' && !a) return runBacktest()
  if (v === 'none' && a)  return runOptimize()
  return runBacktest()
}

watch(validationMode, (mode) => {
  if (mode === 'cpcv' && params.value.autoOptimize) {
    params.value.autoOptimize = false
  }
})

// Task 10 fix round 1: standalone Japan is SINGLE run only (server: 400 japan_single_run_only).
// Pin the form to "no validation, no auto-optimize"; the selector and Advanced runs are hidden.
watch(isSignRuleOnly, (japan) => {
  if (!japan) return
  validationMode.value = 'none'
  params.value.autoOptimize = false
  advancedRunsExpanded.value = false
  // 2026-10-04: the server forces step_aside_mode=pure for lg_japan_index whatever is sent; the
  // Japan preset carries no mode key, so the panel used to fall back to "off", show "Synth-NAV
  // strategy" and hide both band inputs. Pin it so the SELL/BUY thresholds are always visible.
  params.value.stepAsideMode = 'pure'
}, { immediate: true })

const distributionRows = computed(() => {
  const d = activeJob.value?.cpcvResult?.distribution
  if (!d) return {} as Record<string, MetricSummary>
  const rows: Record<string, MetricSummary> = {
    'Sharpe': d.sharpe,
    'Sortino': d.sortino,
    'CAGR': d.cagr,
    'Max DD': d.maxDrawdown,
    'Profit Factor': d.profitFactor,
    'Win Rate': d.winRate,
  }
  if (d.fitness) rows['Fitness'] = d.fitness
  return rows
})

function stdColour(std: number, mean: number): string {
  if (Math.abs(mean) < 1e-9) return '#888'
  const ratio = Math.abs(std / mean)
  if (ratio < 0.2) return '#4caf50'
  if (ratio < 0.5) return '#fbc02d'
  return '#f44336'
}

function pathConsistencyColour(pathSharpe: number): string {
  const d = activeJob.value?.cpcvResult?.distribution.sharpe
  if (!d) return '#888'
  const distFromMean = Math.abs(pathSharpe - d.mean)
  return distFromMean <= d.stdDev ? '#4caf50' : '#fbc02d'
}

// Safety cap: a normal equity curve has ~600 points per year. 200k is 300+ years
// per path — well beyond any legitimate backtest. Above this we skip rendering
// rather than crashing the component with a RangeError on string join.
const CHART_POINTS_CAP = 200_000

const strategyLabel = computed(() => {
  const job = activeJob.value
  if (!job?.parameters) return ''
  let label = 'Rules'
  if (job.parameters.useHedging) label += ' + LQQS Hedge'
  return label
})

const configSummary = computed(() =>
  `${params.value.zWindow}d / ${params.value.tiltBps}bp / ${params.value.vixScale}x`)

const equityCurvePoints = computed(() => {
  const r = currentResult.value
  if (!r?.equityCurve || r.equityCurve.length < 2) return null

  // Defensive: skip rendering if the dataset is pathologically large
  if (r.equityCurve.length > CHART_POINTS_CAP) {
    console.warn(`equityCurvePoints: skipping render, ${r.equityCurve.length} points exceeds cap ${CHART_POINTS_CAP}`)
    return null
  }

  const pts = r.equityCurve
  const bhPts = r.buyAndHoldCurve ?? []
  const unhedgedPts = r.unhedgedEquityCurve ?? []
  const bgaContribPts = (r as any).bgaContributionCurve ?? []
  const W = 500, H = 160, PAD = 10, LEGEND_W = 75

  // Convert NPV to percentage return from start
  const startNpv = pts[0].npv || 1
  const toRet = (npv: number) => ((npv / startNpv) - 1) * 100

  const stratRets = pts.map(p => toRet(p.npv))
  const bhStartNpv = bhPts.length > 0 ? bhPts[0].npv || 1 : 1
  const bhRets = bhPts.map(p => ((p.npv / bhStartNpv) - 1) * 100)
  const unhedgedStartNpv = unhedgedPts.length > 0 ? unhedgedPts[0].npv || 1 : 1
  const unhedgedRets = unhedgedPts.map(p => ((p.npv / unhedgedStartNpv) - 1) * 100)
  const bgaContribStartNpv = bgaContribPts.length > 0 ? bgaContribPts[0].npv || 1 : 1
  const bgaContribRets = bgaContribPts.map((p: any) => ((p.npv / bgaContribStartNpv) - 1) * 100)

  // Scale Y to encompass all curves in % space
  // Defensive: filter out non-finite values — a blown-up backtest can produce
  // Infinity/NaN NPVs that would make maxY infinite and trigger an infinite
  // grid-line loop below (→ RangeError on join).
  const allRets = [...stratRets, ...bhRets, ...unhedgedRets, ...bgaContribRets].filter(Number.isFinite)
  if (allRets.length === 0) {
    console.warn('equityCurvePoints: skipping render, no finite return values')
    return null
  }
  const minY = Math.min(...allRets, 0) - 5
  const maxY = Math.max(...allRets) * 1.1
  const rangeY = maxY - minY || 1
  // Hard safety cap: a legitimate backtest never spans more than ~10000% range.
  // If it does, something is broken upstream — skip rather than crash.
  if (!Number.isFinite(rangeY) || rangeY > 10_000) {
    console.warn(`equityCurvePoints: skipping render, rangeY=${rangeY} outside sane bounds`)
    return null
  }

  const scaleX = (i: number, total: number) => (i / (total - 1)) * W
  const scaleY = (v: number) => H - PAD - ((v - minY) / rangeY) * (H - PAD * 2)

  // Strategy line (solid green)
  const polyline = stratRets.map((r, i) => `${scaleX(i, pts.length).toFixed(1)},${scaleY(r).toFixed(1)}`).join(' ')

  // B&H line (dashed grey)
  let bhPolyline = ''
  if (bhRets.length >= 2) {
    bhPolyline = bhRets.map((r, i) => `${scaleX(i, bhPts.length).toFixed(1)},${scaleY(r).toFixed(1)}`).join(' ')
  }

  // Unhedged line (amber dashed) — strategy decisions on BGA in isolation, no OTPs
  let unhedgedPolyline = ''
  if (unhedgedRets.length >= 2) {
    unhedgedPolyline = unhedgedRets.map((r, i) => `${scaleX(i, unhedgedPts.length).toFixed(1)},${scaleY(r).toFixed(1)}`).join(' ')
  }

  // BGA contribution line (yellow solid) — BGA's share of joint-strategy NPV growth
  let bgaContribPolyline = ''
  if (bgaContribRets.length >= 2) {
    bgaContribPolyline = bgaContribRets.map((r: number, i: number) => `${scaleX(i, bgaContribPts.length).toFixed(1)},${scaleY(r).toFixed(1)}`).join(' ')
  }

  // Zero line
  const zeroY = scaleY(0)

  // Grid lines at regular % intervals
  const gridLines: string[] = []
  const step = rangeY > 100 ? 50 : rangeY > 50 ? 25 : 10
  // Belt-and-braces cap: even with sane inputs, never emit more than 1000 grid lines.
  const maxGridLines = 1000
  let gridIter = 0
  for (let v = Math.ceil(minY / step) * step; v <= maxY && gridIter < maxGridLines; v += step, gridIter++) {
    const y = scaleY(v)
    gridLines.push(`<line x1="0" y1="${y}" x2="${W}" y2="${y}" stroke="#2a2a2a" stroke-width="0.5"/>`)
  }
  // Zero line slightly brighter
  gridLines.push(`<line x1="0" y1="${zeroY}" x2="${W}" y2="${zeroY}" stroke="#444" stroke-width="0.5"/>`)

  // Y axis labels (%)
  const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`
  const yLabels = [
    `<text x="${W + 4}" y="${scaleY(maxY) + 4}" fill="#555" font-size="7">${fmtPct(maxY)}</text>`,
    `<text x="${W + 4}" y="${zeroY + 3}" fill="#666" font-size="7">0%</text>`,
    ...(minY < -5 ? [`<text x="${W + 4}" y="${scaleY(minY) + 4}" fill="#555" font-size="7">${fmtPct(minY)}</text>`] : []),
  ]

  // X axis labels
  const startLabel = pts[0].date.substring(0, 7)
  const midIdx = Math.floor(pts.length / 2)
  const midLabel = pts[midIdx].date.substring(0, 7)
  const endLabel = pts[pts.length - 1].date.substring(0, 7)
  const xLabels = [
    `<text x="0" y="${H}" fill="#555" font-size="8">${startLabel}</text>`,
    `<text x="${W / 2 - 15}" y="${H}" fill="#555" font-size="8">${midLabel}</text>`,
    `<text x="${W - 35}" y="${H}" fill="#555" font-size="8">${endLabel}</text>`,
  ]

  // CAGR: use backend value for strategy (matches KPI card), compute for other curves
  const tradingDays = r.tradingDays || pts.length
  const calendarYears = tradingDays / 252
  const npvToCagr = (startNpv: number, endNpv: number) => {
    if (calendarYears <= 0 || startNpv <= 0) return 0
    return (Math.pow(endNpv / startNpv, 1 / calendarYears) - 1) * 100
  }
  const fmtCagr = (cagr: number) => `${cagr >= 0 ? '+' : ''}${cagr.toFixed(1)}% pa`

  // Strategy CAGR: use backend's cagrPct (matches KPI card exactly)
  const stratCagr = (r.cagrPct ?? 0) * 100

  // BGA Only / B&H: compute from initial cash (50000) to final NPV
  const INITIAL_CASH = 50000
  const bhCagr = bhPts.length > 0 ? npvToCagr(INITIAL_CASH, bhPts[bhPts.length - 1].npv) : 0
  const unhedgedCagr = unhedgedPts.length > 0 ? npvToCagr(INITIAL_CASH, unhedgedPts[unhedgedPts.length - 1].npv) : 0
  const bgaContribCagr = bgaContribPts.length > 0 ? npvToCagr(INITIAL_CASH, bgaContribPts[bgaContribPts.length - 1].npv) : 0

  // Legend with CAGR at end of each line
  const stratFinalRet = stratRets[stratRets.length - 1]
  const stratEndY = scaleY(stratFinalRet)
  const bhFinalRet = bhRets.length > 0 ? bhRets[bhRets.length - 1] : 0
  const bhEndY = bhRets.length > 0 ? scaleY(bhFinalRet) : 0
  const unhedgedFinalRet = unhedgedRets.length > 0 ? unhedgedRets[unhedgedRets.length - 1] : 0
  const unhedgedEndY = unhedgedRets.length > 0 ? scaleY(unhedgedFinalRet) : 0
  const bgaContribFinalRet = bgaContribRets.length > 0 ? bgaContribRets[bgaContribRets.length - 1] : 0
  const bgaContribEndY = bgaContribRets.length > 0 ? scaleY(bgaContribFinalRet) : 0

  // Prevent legend labels from overlapping by spacing them
  const labelPositions: { y: number; label: string; color: string; bold: boolean }[] = [
    { y: stratEndY, label: `BGA + OTP ${fmtCagr(stratCagr)}`, color: '#4caf50', bold: true },
  ]
  if (bgaContribRets.length > 0) {
    labelPositions.push({ y: bgaContribEndY, label: `BGA Contribution ${fmtCagr(bgaContribCagr)}`, color: '#facc15', bold: false })
  }
  if (unhedgedRets.length > 0) {
    labelPositions.push({ y: unhedgedEndY, label: `BGA Only ${fmtCagr(unhedgedCagr)}`, color: '#ff9800', bold: false })
  }
  if (bhRets.length > 0) {
    labelPositions.push({ y: bhEndY, label: `Buy & Hold ${fmtCagr(bhCagr)}`, color: '#666', bold: false })
  }
  // Sort by Y position and space out if overlapping
  labelPositions.sort((a, b) => a.y - b.y)
  for (let i = 1; i < labelPositions.length; i++) {
    if (labelPositions[i].y - labelPositions[i - 1].y < 10) {
      labelPositions[i].y = labelPositions[i - 1].y + 10
    }
  }

  const legend = labelPositions.map(l =>
    `<text x="${W + 4}" y="${l.y + 3}" fill="${l.color}" font-size="7"${l.bold ? ' font-weight="bold"' : ''}>${l.label}</text>`
  )

  return {
    viewBox: `0 0 ${W + LEGEND_W} ${H + 5}`,
    grid: gridLines.join(''),
    polyline,
    bhPolyline,
    hasBh: bhRets.length >= 2,
    unhedgedPolyline,
    hasUnhedged: unhedgedRets.length >= 2,
    bgaContribPolyline,
    hasBgaContrib: bgaContribRets.length >= 2,
    yLabels: yLabels.join(''),
    xLabels: xLabels.join(''),
    legend: legend.join(''),
  }
})

const isJobRunning = computed(() =>
  activeJob.value?.status === 'queued' || activeJob.value?.status === 'running')

// Sentinel activeJob returned to callers after a successful POST /run response.
// The primary purpose is UX: it flips isJobRunning true instantly, disabling the
// Run button in the sub-second window between the HTTP response landing and the
// first poll settling activeJob with the real server-side status. Without this
// a quick double-click (or a queued browser click event) let two jobs launch
// against the same params (2026-08-23 UX report). Overwritten by the first
// successful poll, so no visible fields need to be accurate here.
function queuedSentinel(jobId: string): BacktestJob {
  return { jobId, status: 'queued' } as unknown as BacktestJob
}

const progressPct = computed(() =>
  activeJob.value?.progress?.percentComplete ?? 0)

const progressPhase = computed(() =>
  activeJob.value?.progress?.phase ?? '')

const progressFolds = computed(() => {
  const p = activeJob.value?.progress
  if (!p) return ''
  return `Fold ${p.currentFold}/${p.totalFolds}`
})

const wfResult = computed<WalkForwardResultData | null>(() =>
  activeJob.value?.walkForwardResult ?? null)

const currentFolds = computed(() => {
  if (!wfResult.value) return []
  const folds = wfResult.value.folds ?? []
  const foldResults = wfResult.value.refitFoldResults ?? wfResult.value.fixedFoldResults
  return folds.map((f, i) => ({
    ...f,
    result: foldResults?.[i] ?? null,
  }))
})

const hasFoldResults = computed(() => {
  const wf = wfResult.value
  if (!wf?.folds?.length) return false
  const results = wf.refitFoldResults ?? wf.fixedFoldResults
  return !!results && results.length > 0 && !!results[0]?.equityCurve?.length
})

// ---- Formatting helpers ----

function fmtPct(val: number | null | undefined): string {
  if (val == null) return '--'
  return (val * 100).toFixed(1) + '%'
}

function fmtSharpe(val: number | null | undefined): string {
  if (val == null) return '--'
  return val.toFixed(2)
}

function valueClass(val: number | null | undefined, threshold = 0): string {
  if (val == null) return ''
  return val > threshold ? 'positive' : val < threshold ? 'negative' : ''
}

function decayClass(val: number | null | undefined): string {
  if (val == null) return ''
  const absVal = Math.abs(val)
  if (absVal < 0.20) return 'positive'
  if (absVal < 0.50) return 'decay-warn'
  return 'negative'
}

// ---- API interactions ----

async function loadConfigs() {
  try {
    configs.value = await api.backtestConfigs(btFund.value)
  } catch {
    // configs not available
  }
}

async function loadJobs() {
  try {
    const jobs = await api.backtestJobs(btAsset.value)
    completedJobs.value = jobs.filter(j => j.status === 'completed')
    // If there's a running job, pick it up
    const running = jobs.find(j => j.status === 'queued' || j.status === 'running')
    if (running) {
      activeJob.value = running
      activeJobId.value = running.jobId
      startPolling()
    }
  } catch {
    // jobs endpoint not available
  }
}

// Ensure OTP Reserve binds to a real dropdown option. Since 2026-07-18 null
// IS a valid option ("Sweep (auto)" — the grid searches the reserve axis),
// and 0 means "NO OTP" (pinned fully-invested, never swept). Only NaN and
// undefined (pre-field legacy configs) are coerced to null so they land on
// the sweep option rather than silently inheriting a stale pin.
function normaliseOtpReserve() {
  if (params.value.otpReservePct === undefined || Number.isNaN(params.value.otpReservePct)) {
    params.value.otpReservePct = null
  }
}

// Guards loadConfig's async job fetch against out-of-order resolution
// (2026-07-16): the panel auto-loads the ACTIVE preset on mount; if that
// preset's source job is slow or 404s (e.g. a preset saved without a stored
// result), its catch-path resolved AFTER a user's subsequent preset
// selection and nulled the newer, successfully-loaded activeJob — the
// "preset won't display graphs" symptom.
let loadConfigSeq = 0

async function loadConfig(name: string) {
  if (!configs.value || !name) return
  const entry = configs.value.configs[name]
  if (!entry) return
  // Task 10 fix round 1: Japan is single-run only — a loaded preset never re-enables auto-optimize
  // (defaultParams has it on).
  params.value = { ...defaultParams, ...entry.params, ...(isSignRuleOnly.value ? { autoOptimize: false, stepAsideMode: 'pure' } : {}) }
  normaliseOtpReserve()
  selectedConfig.value = name
  const seq = ++loadConfigSeq
  linkedJobMissing.value = false
  relinkError.value = null

  // Load linked job results if available
  if (entry.source_job_id) {
    try {
      const job = await api.backtestJob(entry.source_job_id)
      if (seq !== loadConfigSeq) return // superseded by a newer selection
      if (job?.status === 'completed') {
        activeJob.value = job
        activeJobId.value = job.jobId
      } else {
        activeJob.value = null
        activeJobId.value = null
        // Job exists but never completed (or was purged and refetched into a
        // ghost row). Signal so the empty-state offers a "Run backtest to link".
        linkedJobMissing.value = true
      }
    } catch {
      if (seq !== loadConfigSeq) return // stale failure must not clobber
      activeJob.value = null
      activeJobId.value = null
      // Fetch failed — most commonly 404 because backtest_results/{id}.json
      // was purged. Show the heal button.
      linkedJobMissing.value = true
    }
  } else {
    activeJob.value = null
    activeJobId.value = null
    // Preset was saved programmatically without a source_job_id — no chart
    // to render. Show the heal button.
    linkedJobMissing.value = true
  }
}

// Chart won't render without a linked backtest_results/{jobId}.json blob.
// Two causes trigger this: (a) preset saved with source_job_id=null (e.g. a
// scratch driver skipped the API's Run path), (b) the linked job's result
// file was purged from disk. `runAndLink()` runs a fresh single-mode backtest
// with the STORED preset's params (not the form's, which may have been
// edited since load) and calls /api/backtest/configs/{name}/relink — that
// endpoint updates only source_job_id, so source_metrics (promotion baseline)
// is preserved (see memory feedback_preset_needs_source_job_id_for_graph).
const linkedJobMissing = ref(false)
const relinking = ref(false)
const relinkError = ref<string | null>(null)

async function runAndLinkSelectedPreset() {
  if (!selectedConfig.value || !configs.value) return
  if (gatedRunBlocked.value) return
  const entry = configs.value.configs[selectedConfig.value]
  if (!entry) return
  relinking.value = true
  relinkError.value = null
  try {
    // Task 10 (F5): the run is keyed by the engine asset id, the relink by the config fund slug.
    const run = await api.backtestRun('single', entry.params, { assetId: btAsset.value, fitnessPreset: 'sharpe_only' })
    const jobId = run.jobId
    // Poll until completed or failed (single-mode BGA backtest ~10-30s on
    // the 1651-day dataset; poll every 3s up to 5 minutes).
    let job: BacktestJob | null = null
    const deadline = Date.now() + 5 * 60_000
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000))
      job = await api.backtestJob(jobId)
      if (job?.status === 'completed' || job?.status === 'failed') break
    }
    if (!job || job.status !== 'completed') {
      throw new Error(job?.error || `Backtest ${job?.status ?? 'timed out'}`)
    }
    await api.backtestRelinkConfig(selectedConfig.value, jobId, btFund.value)
    await loadConfigs()
    await loadConfig(selectedConfig.value)
  } catch (err: unknown) {
    relinkError.value = err instanceof Error ? err.message : String(err)
  } finally {
    relinking.value = false
  }
}

/** The job a save should link. A loaded grid row or a CPCV result has no full-period result of these
 *  exact params on its own job (the grid job's result is its winner's; CPCV has none), so the preset
 *  links to a fresh single run of the form's params instead — run BEFORE the save, so the server stores
 *  that run's metrics in the same write (2026-10-09: the old save-then-relink kept the grid winner's
 *  metrics, and a CPCV save stored none and broke the BGA risk baseline on Apply). */
function needsOwnRunForSave(): boolean {
  const job = activeJob.value
  if (!job) return false
  if (job.type === 'cpcv') return true
  return loadedRow.value != null && loadedRow.value.jobId === job.jobId
}

async function runSingleForSave(p: BacktestParams): Promise<string> {
  const run = await api.backtestRun('single', p, { assetId: btAsset.value, fitnessPreset: 'sharpe_only' })
  const deadline = Date.now() + 5 * 60_000
  let job: BacktestJob | null = null
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 3000))
    job = await api.backtestJob(run.jobId)
    if (job?.status === 'completed' || job?.status === 'failed') break
  }
  if (!job || job.status !== 'completed') throw new Error(job?.error || `backtest ${job?.status ?? 'timed out'}`)
  return run.jobId
}

async function saveConfig(name: string) {
  if (!name) return
  const formParams = { ...params.value }
  try {
    let sourceJobId = activeJob.value?.jobId
    if (needsOwnRunForSave()) {
      liveAppliedBanner.value = `Saving ${name}: running a single backtest of these parameters to link…`
      sourceJobId = await runSingleForSave(formParams)
    }
    try {
      await api.backtestSaveConfig(name, formParams, sourceJobId, btFund.value)
    } catch (e: any) {
      // Overwrite guard (2026-08-12 pinned-preset drift): the backend 409s
      // a save over an existing name. Make the overwrite an explicit,
      // operator-confirmed act — silently re-stamping pinned presets is how
      // the 2026-05-18 references drifted.
      if (!/already exists/i.test(e?.message ?? '')) throw e
      if (!window.confirm(
        `Preset '${name}' already exists. Overwrite it with the current parameters? `
        + `The previous values will be replaced (this is event-logged).`)) {
        liveAppliedBanner.value = ''
        return
      }
      await api.backtestSaveConfig(name, formParams, sourceJobId, btFund.value, true)
    }
    await loadConfigs()
    selectedConfig.value = name
    showSaveInput.value = false
    showResultSave.value = false
    saveConfigName.value = ''
    liveAppliedBanner.value = sourceJobId && sourceJobId !== activeJob.value?.jobId
      ? `Config saved: ${name} — linked to its own backtest.`
      : `Config saved: ${name}`
    setTimeout(() => { liveAppliedBanner.value = '' }, 5000)
  } catch (e: any) {
    liveAppliedBanner.value = ''
    error.value = `Failed to save '${name}': ${e?.message ?? e}`
  }
}

// Results panel "Save Config" button: immediate prompt → save.
// Previously this toggled an inline input row below the buttons, which was
// easy to miss (especially after scrolling into the TPE leaderboard) and
// looked like the button was doing nothing.
async function promptSaveConfigFromResults() {
  // Defence-in-depth guard. The button is :disabled by canSaveFromResults
  // but in case a future caller (or test) bypasses the button, refuse to
  // persist a form state that doesn't match the displayed result.
  if (activeJob.value?.parameters) {
    const diffs = formDiffsFromActiveJob()
    if (diffs.length > 0) {
      const sample = diffs.slice(0, 5).join(', ') + (diffs.length > 5 ? `, +${diffs.length - 5} more` : '')
      error.value = `Form differs from the scored result on: ${sample}. `
        + `Click 'Load' on the result row you want to save, then Save. `
        + `(This guard exists because the same mismatch wrote bad params under 'databento_v3' on 2026-05-19.)`
      return
    }
  }
  // Suggest a default name from the TPE winner group label if we're looking
  // at a full-optimize result, falling back to the existing selectedConfig.
  let defaultName = selectedConfig.value || ''
  const best = activeJob.value?.tpeResult?.overallBest
  if (best && !defaultName) {
    defaultName = `tpe_${best.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`
  }
  const raw = window.prompt('Save current params as config:', defaultName)
  if (raw === null) return  // user cancelled
  const name = raw.trim()
  if (!name) {
    error.value = 'Config name cannot be blank'
    return
  }
  await saveConfig(name)
}

async function renameConfig() {
  const oldName = selectedConfig.value
  const newName = renameConfigName.value?.trim()
  if (!oldName || !newName || oldName === newName) return
  if (isSignRuleOnly.value && configs.value?.active === oldName) {
    error.value = `'${oldName}' is ${signRuleCopy.value.fund}'s active preset (drives the live 08:40 threshold) — save under a new name and Apply to Live instead of renaming it.`
    return
  }
  try {
    // Save current config under new name, then delete old
    const store = configs.value
    const existingEntry = store?.configs?.[oldName]
    if (existingEntry) {
      await api.backtestSaveConfig(newName, existingEntry.params, existingEntry.source_job_id ?? undefined,
        btFund.value)
      await api.backtestDeleteConfig(oldName, btFund.value)
      await loadConfigs()
      selectedConfig.value = newName
    }
    showRenameInput.value = false
    renameConfigName.value = ''
  } catch (e: any) {
    error.value = e.message ?? 'Failed to rename config'
  }
}

async function deleteConfig(name: string) {
  if (!name) return
  try {
    await api.backtestDeleteConfig(name, btFund.value)
    await loadConfigs()
    if (selectedConfig.value === name) selectedConfig.value = ''
  } catch (e: any) {
    error.value = e.message ?? 'Failed to delete config'
  }
}

// Cancel the currently in-flight backtest via DELETE /api/backtest/jobs/{id}.
// The server-side BacktestJobService.CancelJob only transitions QUEUED jobs
// to failed — a job that has already started running keeps running (needs
// CancellationToken threading through the run pipeline, deferred). We don't
// optimistically clear activeJob here; the next poll will settle the real
// status. If the cancel succeeded, status flips to 'failed' → isJobRunning
// becomes false → the Run button re-enables naturally.
async function cancelBacktest() {
  const id = activeJobId.value
  if (!id) return
  try {
    await api.backtestCancelJob(id)
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to cancel backtest'
  }
}

async function runBacktest(opts: { installSentinel?: boolean } = {}) {
  const installSentinel = opts.installSentinel ?? true
  error.value = null
  // 2026-09-15: refuse buy < sell here — server refuses too but this
  // surfaces the error immediately without a round trip.
  if (stepAsideBandError.value) {
    error.value = stepAsideBandError.value
    return
  }
  if (gatedRunBlocked.value) {
    error.value = `Running a ${signRuleCopy.value.fund} backtest requires the ${signRuleCopy.value.grant} grant.`
    return
  }
  loading.value = true
  try {
    const type = walkForwardEnabled.value && !isSignRuleOnly.value
      ? (wfFullGrid.value ? 'walk-forward' : 'walk-forward-quick')
      : 'single'
    const runParams: BacktestParams = { ...params.value }
    const resp = await api.backtestRun(type, runParams, { assetId: btAsset.value })
    activeJobId.value = resp.jobId
    // Install the sentinel only for user-initiated runs; auto-run at mount
    // must not overwrite a loadConfig-set activeJob when its POST resolves.
    if (installSentinel && activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start backtest'
  } finally {
    loading.value = false
  }
}

async function runOptimize() {
  error.value = null
  loading.value = true
  try {
    const type = walkForwardEnabled.value ? 'optimize-wf' : 'optimize'
    const runParams: BacktestParams = { ...params.value }
    const resp = await api.backtestRun(type, runParams, {
      fitnessPreset: fitnessPreset.value,
      topN: 10,
      trainDays: trainDays.value,
      testDays: testDays.value,
      stepDays: 60,
      assetId: btAsset.value,
    })
    activeJobId.value = resp.jobId
    // Only install the sentinel when nothing else has taken activeJob
    // during the await window. Guards against onMounted's auto-run
    // (BacktestPanel.vue:1290) clobbering a loadConfig-set or test-set
    // completed job that landed between the POST fire-and-await.
    if (activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start optimization'
  } finally {
    loading.value = false
  }
}

/** 2026-10-07 (Paolo, Charteris): the walk-forward band check for a sign-rule fund — the band-only focused
 *  grid (server BuildFocusedGrid: SELL × BUY band, sign rule re-applied per combo) on 250/60-day folds, with
 *  OOS columns and the sensitivity heatmap. The form itself stays single-run. */
async function runBandWalkForward() {
  error.value = null
  loading.value = true
  try {
    // A blank OTP reserve means "sweep it" (×5 combos); sign-rule funds have no OTP legs, so pin the
    // operational value (0 when blank) — 2026-10-07 a Charteris check ran 180 combos instead of 36.
    const runParams: BacktestParams = {
      ...params.value, autoOptimize: true, stepAsideMode: 'pure', otpReservePct: params.value.otpReservePct ?? 0,
    }
    const resp = await api.backtestRun('optimize-wf-focused', runParams, {
      fitnessPreset: 'dsr_aware',
      topN: 10,
      trainDays: 250,
      testDays: 60,
      stepDays: 60,
      assetId: btAsset.value,
    })
    activeJobId.value = resp.jobId
    if (activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start the walk-forward band check'
  } finally {
    loading.value = false
  }
}

async function runOptimizeFocused() {
  error.value = null
  loading.value = true
  try {
    const type = sweepOverlaySc.value ? 'optimize-wf-overlay' : 'optimize-wf-focused'
    const runParams: BacktestParams = { ...params.value }
    const resp = await api.backtestRun(type, runParams, {
      fitnessPreset: 'dsr_aware',
      topN: 10,
      trainDays: trainDays.value,
      testDays: testDays.value,
      stepDays: 60,
      sweepOverlaySc: sweepOverlaySc.value,
      overlaySweepBudget: overlaySweepBudget.value,
      assetId: btAsset.value,
    })
    activeJobId.value = resp.jobId
    // Only install the sentinel when nothing else has taken activeJob
    // during the await window. Guards against onMounted's auto-run
    // (BacktestPanel.vue:1290) clobbering a loadConfig-set or test-set
    // completed job that landed between the POST fire-and-await.
    if (activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start focused optimization'
  } finally {
    loading.value = false
  }
}

async function runFullOptimize() {
  error.value = null
  loading.value = true
  try {
    const resp = await api.backtestRun('full-optimize', { ...params.value }, {
      fitnessPreset: fitnessPreset.value,
      trainDays: trainDays.value,
      testDays: testDays.value,
      stepDays: 60,
      tpeTrialBudget: tpeTrialBudget.value,
      assetId: btAsset.value,
    })
    activeJobId.value = resp.jobId
    // Only install the sentinel when nothing else has taken activeJob
    // during the await window. Guards against onMounted's auto-run
    // (BacktestPanel.vue:1290) clobbering a loadConfig-set or test-set
    // completed job that landed between the POST fire-and-await.
    if (activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start full optimization'
  } finally {
    loading.value = false
  }
}

/** When the source params carry otpReservePct=null, surface the
 *  *operational* reserve value (`otpReservePctOrFallback`, which the
 *  C# engine resolves to the strategy's actually-deployed reserve %)
 *  in the form. Avoids:
 *    - blank field (looks broken, ambiguous)
 *    - cosmetic-default 0.33 clobber (the 2026-05-19 databento_v3/v4
 *      bug — silently overwrote a 0%-reserve winner with 33% reserve,
 *      costing ~1.0 Sharpe / ~30pp CAGR vs the displayed metrics).
 *  Show what the strategy actually used. */
function resolveOtpReserveForForm(src: any): number {
  if (src.otpReservePct != null && !Number.isNaN(src.otpReservePct)) return src.otpReservePct
  if (src.otpReservePctOrFallback != null) return src.otpReservePctOrFallback
  return 0
}

// Which TPE group (if any) is currently backfilling a full-engine single run.
// Non-null while the run is in flight → the row's Load button shows
// "Running…" and every other Load button is disabled to prevent double-fires.
// See fallback branch in loadTpeWinner below.
const tpeGroupBackfilling = ref<{ label: string } | null>(null)

async function loadTpeWinner(group: any) {
  const merged = { ...defaultParams, ...group.bestParams } as any
  merged.otpReservePct = resolveOtpReserveForForm(group.bestParams)
  params.value = merged
  selectedConfig.value = ''
  if (group.fullResult && activeJob.value) {
    activeJob.value = {
      ...activeJob.value,
      result: group.fullResult,
      // Clear walkForwardResult so KPI cards show the loaded group's metrics
      walkForwardResult: undefined,
    }
    return
  }
  // Fallback (2026-09-18): the full-engine detail pass in
  // BacktestJobService.RunFullOptimizeAsync only populates group.fullResult
  // for the top-10 groups. Clicking Load on any group beyond top 10 used to
  // silently skip the result-panel update, leaving stale KPI tiles / equity
  // curve on screen — user sees no visible change. Now we run a fresh
  // single-mode backtest with the group's params and patch the result in.
  if (tpeGroupBackfilling.value) return   // guard: already backfilling
  tpeGroupBackfilling.value = { label: group.label ?? '(unnamed)' }
  error.value = null
  try {
    const runResp = await api.backtestRun('single', merged, {
      assetId: btAsset.value,
      fitnessPreset: 'sharpe_only',
    })
    const jobId = runResp.jobId
    const startedAt = Date.now()
    // Poll every 3s up to 5min.
    while (Date.now() - startedAt < 5 * 60 * 1000) {
      await new Promise(r => setTimeout(r, 3000))
      const job: any = await api.backtestJob(jobId)
      if (job?.status === 'completed') {
        if (activeJob.value) {
          activeJob.value = {
            ...activeJob.value,
            result: job.result,
            walkForwardResult: undefined,
            // Preserve tpeResult so the leaderboard stays visible.
            tpeResult: activeJob.value.tpeResult,
          } as any
        }
        return
      }
      if (job?.status === 'failed') {
        error.value = `Detail-pass backtest failed for group '${group.label ?? '(unnamed)'}': ${job.error ?? 'unknown error'}`
        return
      }
    }
    error.value = `Detail-pass backtest for group '${group.label ?? '(unnamed)'}' timed out after 5 min — check the Jobs list.`
  } catch (e: any) {
    error.value = `Detail-pass backtest failed for group '${group.label ?? '(unnamed)'}': ${e?.message ?? e}`
  } finally {
    tpeGroupBackfilling.value = null
  }
}

/** Copy an optimizer-result row's params into the form. Mirrors
 *  loadTpeWinner. Resolves null otpReservePct to the row's actual
 *  operational value via resolveOtpReserveForForm. */
function loadOptimizerRow(rowParams: BacktestParams, row?: RankedResult) {
  const merged = { ...defaultParams, ...rowParams } as any
  merged.otpReservePct = resolveOtpReserveForForm(rowParams)
  params.value = merged
  selectedConfig.value = ''
  const job = activeJob.value
  if (row && job && row.fullResult?.equityCurve?.length) {
    loadedRow.value = { jobId: job.jobId, rank: row.rank, params: rowParams, result: row.fullResult }
    liveAppliedBanner.value = `Row #${row.rank} loaded — the KPIs and equity curve now show it. Save / Apply when ready.`
  } else {
    liveAppliedBanner.value = 'Row loaded into form. Save / Apply when ready.'
  }
  setTimeout(() => { liveAppliedBanner.value = '' }, 5000)
}

function backToGridWinner() {
  loadedRow.value = null
}

async function runCpcv() {
  error.value = null
  loading.value = true
  try {
    const runParams: BacktestParams = { ...params.value }
    const resp = await api.backtestRun('cpcv', runParams, {
      cpcvN: cpcvN.value,
      cpcvK: cpcvK.value,
      cpcvEmbargoDays: cpcvEmbargoDays.value,
      assetId: btAsset.value,
    })
    activeJobId.value = resp.jobId
    // Only install the sentinel when nothing else has taken activeJob
    // during the await window. Guards against onMounted's auto-run
    // (BacktestPanel.vue:1290) clobbering a loadConfig-set or test-set
    // completed job that landed between the POST fire-and-await.
    if (activeJob.value === null) {
      activeJob.value = queuedSentinel(resp.jobId)
    }
    startPolling()
  } catch (e: any) {
    error.value = e.message ?? 'Failed to start CPCV'
  } finally {
    loading.value = false
  }
}

// When walk-forward job completes, load the winning params into the UI
function onJobCompleted(job: BacktestJob) {
  if (job.walkForwardResult) {
    // The walk-forward fixed params are the best-fit result
    const wf = job.walkForwardResult
    if (wf.fixedParams) {
      // Load winning params into the UI (only when auto-optimize is on).
      // 2026-05-19: do NOT call normaliseOtpReserve() here — it would
      // clobber a legitimate winner null with the cosmetic 0.33 default,
      // the bug that produced databento_v3/v4. Instead resolve null to
      // the operational reserve % the engine actually used.
      if (params.value.autoOptimize) {
        Object.assign(params.value, wf.fixedParams)
        params.value.otpReservePct = resolveOtpReserveForForm(wf.fixedParams)
      }
      // Auto-save as "best_fit" config — never for Japan (single-run only, and nothing may be
      // written into backtest_configs/japan.json without an operator's explicit Save).
      if (isSignRuleOnly.value) return
      api.backtestSaveConfig('best_fit', { ...params.value }, job.jobId,
        btFund.value).then(() => loadConfigs())
    }
  }
}

async function scheduleBacktest() {
  error.value = null
  try {
    const type = walkForwardEnabled.value
      ? (wfFullGrid.value ? 'walk-forward' : 'walk-forward-quick')
      : 'single'
    // Task 10 (F6): scheduling is hidden for a sign-rule-only fund (no standalone schedule; the
    // /schedule whitelist excludes it). isSignRuleOnly decides the gate; the cast below is
    // type-only narrowing to satisfy backtestSchedule's existing parameter type.
    if (isSignRuleOnly.value) return
    const fund = btFund.value as Exclude<BacktestConfigFund, 'japan' | 'charteris' | 'amati' | 'miners' | 'japanesg' | 'tech' | 'electrum' | 'jhtech' | 'asiatech' | 'baringsoil' | 'spworld' | 'smallcap'>
    await api.backtestSchedule(cronExpression.value, type, { ...params.value }, fund)
    showScheduleInput.value = false
  } catch (e: any) {
    error.value = e.message ?? 'Failed to schedule backtest'
  }
}

const liveAppliedBanner = ref('')

// 2026-05-17 UX fix: previously "Apply to Live" silently saved the FORM's
// current values into a fixed preset named `production`, then activated
// `production`. The dropdown selection was IGNORED. This broke the
// K_t-canonical promotion on 2026-05-17 07:33 UTC — user selected
// databento_kt_canonical_v1 from the dropdown but the form hadn't loaded
// the preset's params, so Apply round-tripped stale form state to live.
//
// New semantics: activate the SELECTED preset by name via the canonical
// promote endpoint. Requires the form to match the selected preset (no
// in-flight edits). To apply edited params, user must Save as a named
// preset first, then Apply.

/** Apply the same on-load normalisations that `loadConfig` does, so the
 *  diff is comparing apples-to-apples. Without this, a preset with
 *  `otpReservePct=null` looks "different" from the form (which has the
 *  normalised 0.33 default) and falsely blocks the Apply button. */
function normaliseForCompare(p: any): any {
  // 2026-05-19: previously this coerced null → 0.33 on `otpReservePct`,
  // which made the form-vs-result guard treat explicit-null (winner
  // deploys 100% of NAV) and explicit-0.33 (33% cash reserve) as the
  // same value. They aren't — operationally the OtpReservePctOrFallback
  // resolves to 0 vs 0.33, and the difference is ~1.0 Sharpe / ~30pp
  // CAGR in the sweep. We now preserve the value as-is so the diff
  // surfaces the drift instead of hiding it.
  //
  // This function serves `formDiffsFromActiveJob` (Save-from-results
  // gate) ONLY. The Apply-to-Live gate uses `mirrorLoadConfigSide` below.
  return { ...p }
}

/** Mirror of `loadConfig`'s normalisations (line 736-742) applied
 *  symmetrically to the saved-preset side of the Apply-to-Live diff.
 *  loadConfig spreads defaults + preset then calls `normaliseOtpReserve`,
 *  so a preset with `otpReservePct=null` ends up as 0.33 in the form.
 *  Without mirroring this on the expected side, every `otpReservePct=null`
 *  preset (3 of 8 in Prod as of 2026-05-25 — `databento_kt_canonical_v1`,
 *  `event_verify_warm_start_2026_05_18`, `event_verify_tuned_best_2026_05_18`)
 *  fires a spurious diff and the Apply button stays greyed out forever.
 *
 *  This is deliberately SEPARATE from `normaliseForCompare`: that guard
 *  has the opposite requirement (preserve null-vs-0.33 distinction so
 *  Save-from-results doesn't accidentally persist 0.33 over an explicit
 *  null in the scored job). The 2026-05-19 unification of these two
 *  consumers into a single helper was the regression — see
 *  `feedback_two_consumers_same_diff_function` if it exists, or the
 *  symmetry rule: any field `loadConfig` normalises must also be
 *  normalised on the expected side of `formDiffsFromSelected`. */
function mirrorLoadConfigSide(p: any): any {
  const out = { ...p }
  // Mirror normaliseOtpReserve: since 2026-07-18 null is a REAL value
  // ("Sweep (auto)") and 0 is "NO OTP"; only undefined/NaN normalise (to
  // null). Keep this in lockstep with normaliseOtpReserve or the Apply
  // gate re-breaks (2026-05-25 regression class).
  if (out.otpReservePct === undefined || Number.isNaN(out.otpReservePct))
    out.otpReservePct = null
  return out
}

/** Fields in `params.value` that differ from the selected preset's saved
 *  params. Used both to gate Apply and to show a clear diff in the UI.
 *
 *  Compared with both sides normalised (matching what loadConfig does)
 *  and with fields whose default is `undefined` in the form (i.e. fields
 *  the panel doesn't surface as inputs — e.g. synthNav945Method) skipped:
 *  they round-trip through params.value unchanged, so they can't be
 *  "edited" from the form anyway. */
function formDiffsFromSelected(): string[] {
  const sel = selectedConfig.value
  if (!sel || !configs.value) return []
  const entry = configs.value.configs[sel]
  if (!entry?.params) return []
  // Mirror loadConfig's normalisation on the saved-preset side; the form
  // side has already been normalised at load time by `loadConfig`, so it
  // doesn't need re-normalising here.
  const expected = mirrorLoadConfigSide({ ...defaultParams, ...entry.params })
  const actual = { ...params.value }
  const out: string[] = []
  for (const k of Object.keys(expected)) {
    const a = JSON.stringify((actual as any)[k] ?? null)
    const b = JSON.stringify((expected as any)[k] ?? null)
    if (a !== b) out.push(k)
  }
  return out
}

const canApplyToLive = computed(() => {
  if (!selectedConfig.value || !configs.value) return false
  if (!configs.value.configs[selectedConfig.value]) return false
  return formDiffsFromSelected().length === 0
})

/** Fields in `params.value` that differ from the params the active job was
 *  actually scored on (i.e. `activeJob.parameters`). Used to gate the
 *  Save-from-results flow so we never persist a form state that doesn't
 *  match the displayed metrics.
 *
 *  For sweep jobs the displayed *top-level* metrics correspond to the job's
 *  input baseline (activeJob.parameters). Individual winner-row metrics in
 *  the optimizer leaderboard correspond to that row's bestParams — the
 *  user loads them into the form via the "Load" button on each row, which
 *  collapses this diff to zero before save.
 *
 *  This guard exists because Apply-to-Live's twin guard (formDiffsFromSelected)
 *  was added 2026-05-17 but the Save flow was never gated the same way —
 *  silently persisting form state under a sweep-result name was how
 *  `databento_v3` got the wrong params on 2026-05-19. */
function formDiffsFromActiveJob(): string[] {
  const job = activeJob.value
  if (!job?.parameters) return []
  // A loaded grid row is what the KPIs show, so it is what Save must match (2026-10-07).
  const row = loadedRow.value && loadedRow.value.jobId === job.jobId ? loadedRow.value : null
  const scored = row ? { ...row.params, otpReservePct: resolveOtpReserveForForm(row.params) } : job.parameters
  const expected = normaliseForCompare({ ...defaultParams, ...scored })
  const actual = normaliseForCompare(params.value)
  const out: string[] = []
  for (const k of Object.keys(expected)) {
    const a = JSON.stringify((actual as any)[k] ?? null)
    const b = JSON.stringify((expected as any)[k] ?? null)
    if (a !== b) out.push(k)
  }
  return out
}

const canSaveFromResults = computed(() => {
  if (!activeJob.value?.parameters) return true  // pre-job edits — save anything
  return formDiffsFromActiveJob().length === 0
})

const saveFromResultsLabel = computed(() => {
  if (!activeJob.value) return 'Save as Config'
  const diffs = formDiffsFromActiveJob()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 3).join(', ') + (diffs.length > 3 ? `, +${diffs.length - 3} more` : '')
    return `Form differs from scored result (${sample}) — click Load on the row you want, then Save`
  }
  return 'Save as Config'
})

const applyToLiveLabel = computed(() => {
  const sel = selectedConfig.value
  if (!sel) return 'Apply to Live — select a preset'
  const diffs = formDiffsFromSelected()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 3).join(', ') + (diffs.length > 3 ? `, +${diffs.length - 3} more` : '')
    return `Form differs from '${sel}' (${sample}) — Save first`
  }
  return isSignRuleOnly.value
    ? `Apply '${sel}' to Live (sets ${signRuleCopy.value.fund}'s 08:40 ${signRuleCopy.value.futures} threshold)`
    : `Apply '${sel}' to Live`
})

async function applyToLiveTrading() {
  error.value = null
  liveAppliedBanner.value = ''
  // 2026-10-04: JAPAN promotes like every fund (server: JapanPresetPromotion, config:mr:japan only).
  // It still needs the JAPAN grant — without it the panel is read-only and the server 404s.
  if (gatedRunBlocked.value) {
    error.value = `Applying a ${signRuleCopy.value.fund} preset to live requires the ${signRuleCopy.value.grant} grant.`
    return
  }

  const sel = selectedConfig.value
  if (!sel) {
    error.value = 'Select a preset from the dropdown before applying to live.'
    return
  }
  if (!configs.value?.configs[sel]) {
    error.value = `Preset '${sel}' not found in the loaded config store. Reload the page.`
    return
  }
  const diffs = formDiffsFromSelected()
  if (diffs.length > 0) {
    const sample = diffs.slice(0, 5).join(', ') + (diffs.length > 5 ? '…' : '')
    error.value = `Form has ${diffs.length} unsaved edit${diffs.length === 1 ? '' : 's'} vs '${sel}' (${sample}). `
      + `Save the form as a new preset first, or reselect '${sel}' from the dropdown to discard edits.`
    return
  }

  try {
    // Canonical promote endpoint. Atomic Redis write + read-back verify
    // + file active-flag update. Matches the path that was originally
    // intended for the UI button.
    const resp = await fetch(
      `/api/backtest/configs/active/${encodeURIComponent(sel)}?fund=${btFund.value}`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!resp.ok) {
      const text = await resp.text().catch(() => '')
      throw new Error(`Promote failed: ${resp.status} ${text || resp.statusText}`)
    }

    // OTP/BGA divorce Phase 1, Task 4: the sync-write that used to push
    // params.value.otpReservePct/current.otpAccountId into the live
    // user_execution_config on promotion is retired — both fields are gone
    // from the live execution-config surface (Task 3) and OTP sizing is
    // now residual-funded, not operator-set. This preset field
    // (BacktestParams.otpReservePct, the backtest-sweep reserve axis) is
    // otherwise untouched — out of this arc (Phase 2 decision).

    await loadConfigs()
    liveAppliedBanner.value = `Active preset → ${sel}`
    setTimeout(() => { liveAppliedBanner.value = '' }, 8000)
  } catch (e: any) {
    error.value = e.message ?? 'Failed to apply to live trading'
  }
}

// ---- Polling ----

function startPolling() {
  stopPolling()
  pollTimer = setInterval(pollJob, 2000)
  pollJob()
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function pollJob() {
  if (!activeJobId.value) return
  try {
    const job = await api.backtestJob(activeJobId.value)
    activeJob.value = job
    if (job.status === 'completed' || job.status === 'failed') {
      stopPolling()
      if (job.status === 'completed') {
        onJobCompleted(job)
      }
      if (job.status === 'failed') {
        error.value = job.error ?? 'Backtest failed'
      }
    }
  } catch {
    stopPolling()
  }
}

// ---- Lifecycle ----

onMounted(async () => {
  await loadConfigs()
  await loadJobs()

  // Load the active config into the UI on page load
  if (configs.value?.active && configs.value.configs[configs.value.active]) {
    await loadConfig(configs.value.active)
  }

  // Only show results from a different job if loadConfig didn't already set one
  // (i.e. the active config has no linked job). This prevents overwriting
  // config-linked results with an unrelated most-recent job.
  if (!activeJob.value) {
    const jobs = await api.backtestJobs(btAsset.value)
    // Task 10 fix round 1: no mount auto-run for a caller who cannot run Japan.
    if (jobs.length === 0 && configs.value?.active && !gatedRunBlocked.value) {
      // Auto-run at mount is machine-initiated — no double-click UX concern
      // applies, and installing the queued-sentinel here would clobber any
      // subsequent loadConfig-set activeJob when the awaited POST resolves.
      runBacktest({ installSentinel: false })
    } else if (jobs.length > 0) {
      const lastCompleted = jobs.filter(j => j.status === 'completed')[0]
      if (lastCompleted) {
        activeJob.value = lastCompleted
        activeJobId.value = lastCompleted.jobId
      }
    }
  }
})

onUnmounted(() => {
  stopPolling()
})

// Fund switch reloads the fund's own preset store + jobs (parity Phase 5:
// configs are per-fund now — without this, the JH view kept BGA's presets).
watch(portfolio, async () => {
  await loadConfigs()
  await loadJobs()
  if (configs.value?.active && configs.value.configs[configs.value.active]) {
    await loadConfig(configs.value.active)
  }
})

// Exposed for unit tests only; no-op in production bundles.
defineExpose({ validationMode, params, activeJob, loadConfig, sweepOverlaySc, loadTpeWinner, tpeGroupBackfilling, advancedRunsExpanded,
  loadOptimizerRow, currentResult, saveConfig })
</script>

<template>
  <section class="backtest-panel">
    <!-- Auth state diagnostic: show loading or not-authorised message instead of silent blank -->
    <div v-if="authLoading" class="auth-state-msg">Loading authentication…</div>
    <div v-else-if="!isAuthenticated" class="auth-state-msg">Not logged in — sign in on the Account tab to use the backtester.</div>
    <div v-else-if="!isSuperUser" class="auth-state-msg">
      Backtest controls are restricted to superusers.<br>
      <small>Signed in as <strong>{{ user?.email ?? 'unknown' }}</strong> (role: <strong>{{ user?.role ?? 'none' }}</strong>)</small>
    </div>
    <template v-else>
    <!-- Controls Panel (left) -->
    <div class="controls-panel" :class="{ collapsed: controlsCollapsed }">
      <div class="controls-header" @click="controlsCollapsed = !controlsCollapsed">
        <span class="controls-title">
          Backtest Controls
          <span class="portfolio-pill" :class="`pill-${portfolioId}`">{{ portfolioId.toUpperCase() }}</span>
        </span>
        <span class="config-summary">{{ configSummary }}</span>
        <span class="collapse-icon">{{ controlsCollapsed ? '+' : '-' }}</span>
      </div>

      <div class="controls-body" v-show="!controlsCollapsed">
        <!-- Configs section -->
        <div class="section">
          <div class="section-header">Configs</div>
          <div class="config-row">
            <select v-model="selectedConfig" @change="loadConfig(selectedConfig)" class="config-select">
              <option value="">— Load config —</option>
              <option v-for="name in visibleConfigNames" :key="name" :value="name">
                {{ name }}{{ name === configs?.active ? ' ★' : '' }}
              </option>
            </select>
          </div>
          <div class="config-actions">
            <button class="btn-sm" @click="showSaveInput = !showSaveInput"
                    :disabled="selectedIsLocked"
                    :title="selectedIsLocked ? 'System-locked preset (test fixture) — cannot be overwritten. Save under a different name.' : ''">Save</button>
            <button class="btn-sm" @click="showRenameInput = !showRenameInput" :disabled="!selectedConfig">Rename</button>
            <button class="btn-sm btn-danger" @click="deleteConfig(selectedConfig)"
                    :disabled="!selectedConfig || selectedIsLocked"
                    :title="selectedIsLocked ? 'System-locked preset (test fixture) — cannot be deleted.' : ''">Delete</button>
            <!-- 2026-05-17: Apply-to-Live moved out of the results-only action
                 bar. The original placement required a completed backtest job
                 with `result`/`walkForwardResult`/etc. to be visible. Presets
                 imported from TPE harness output have no backing job, so the
                 button was unreachable from the config dropdown. Adding it
                 here means: select preset → Apply, one motion, no run needed. -->
            <!-- 2026-10-04: JAPAN promotes too — it writes config:mr:japan, the live 08:40 NIY threshold. -->
            <button class="btn-sm btn-warn"
                    @click="applyToLiveTrading"
                    :disabled="loading || !canApplyToLive"
                    :title="applyToLiveLabel"
                    data-testid="apply-to-live-config-btn">
              {{ applyToLiveLabel }}
            </button>
          </div>
          <div v-if="showSaveInput" class="config-save-row">
            <input v-model="saveConfigName" class="text-input" placeholder="Config name" @keyup.enter="saveConfig(saveConfigName)" />
            <button class="btn-sm" @click="saveConfig(saveConfigName)">Save</button>
          </div>
          <div v-if="showRenameInput && selectedConfig" class="config-save-row">
            <input v-model="renameConfigName" class="text-input" :placeholder="selectedConfig" @keyup.enter="renameConfig" />
            <button class="btn-sm" @click="renameConfig">Rename</button>
          </div>
        </div>

        <!-- 1) Hypothesis (new top-level section) -->
        <div class="section hypothesis-section">
          <div class="section-header">Hypothesis</div>

          <label class="control-label">IMR Mode
            <span class="info-icon" @click.stop="toggleTooltip('imrMode')">i</span>
          </label>
          <select v-model="params.imrMode" class="config-select">
            <option value="off">Off</option>
            <option value="momentum">Momentum</option>
            <option value="tilt">Tilt</option>
            <option value="filter">Filter</option>
            <option value="both">Both</option>
          </select>
          <div v-if="activeTooltip === 'imrMode' && tooltips.imrMode" class="tooltip-box">
            {{ tooltips.imrMode }}
          </div>

          <label class="control-label checkbox-label">
            <input type="checkbox" v-model="params.noHold" />
            No Hold Zone
            <span class="info-icon" @click.stop="toggleTooltip('noHold')">i</span>
          </label>
          <div v-if="activeTooltip === 'noHold' && tooltips.noHold" class="tooltip-box">
            {{ tooltips.noHold }}
          </div>

          <!-- IMR mom-gate dead-zone (2026-04-28). Production uses 0.10 to suppress
               wz noise-band flips. Surfaced here so a saved config that already
               carries the value is visible/editable, and so an A/B run can sweep
               the same way the CLI harness does. -->
          <div class="param-row">
            <label class="control-label">
              IMR Mom-Gate Dead-Zone
              <span class="info-icon" @click.stop="toggleTooltip('imrMomGateDeadZone')">i</span>
            </label>
            <input type="number" v-model.number="params.imrMomGateDeadZone" class="num-input" min="0" max="0.5" step="0.01" />
          </div>
          <div v-if="activeTooltip === 'imrMomGateDeadZone'" class="tooltip-box">
            Widens the wz momentum-gate threshold by this many z-units to suppress
            noise-band flips. The gate fires SELL only when wz ≤ raw_threshold − dead_zone.
            Production currently uses 0.10 (A/B finds +5.7% CAGR at same DD on focusgrid_max_cagr).
            0 = original tight-zero behaviour.
          </div>

          <!-- News sentiment v1 (2026-04-28). Default-OFF; enabling here AND
               saving the config is the proper backtest→live promotion path. -->
          <div class="subsection">
            <label class="control-label checkbox-label">
              <input type="checkbox" v-model="params.newsSentimentFilter" />
              News Sentiment Filter
              <span class="info-icon" @click.stop="toggleTooltip('newsSentiment')">i</span>
            </label>
            <div v-if="activeTooltip === 'newsSentiment'" class="tooltip-box">
              Blocks BUYs (buy_only) or all decisions (both) on days where today's
              basket-weighted news sentiment is at or below threshold AND coverage
              is above the floor. Coverage acts as a noise gate — low-coverage days
              are passed through. A/B (2026-04-28) finds thr=0.15 cov=30 buy_only
              gives +0.07 Sharpe / +2.7pp CAGR / same DD on focusgrid_max_cagr.
              Sentiment is sourced from AV NEWS_SENTIMENT, aggregated daily.
            </div>
            <div v-if="params.newsSentimentFilter" class="param-row">
              <label class="control-label">Threshold</label>
              <input type="number" v-model.number="params.newsSentimentThreshold" class="num-input" min="-1" max="1" step="0.01" />
            </div>
            <div v-if="params.newsSentimentFilter" class="param-row">
              <label class="control-label">Min coverage %</label>
              <input type="number" v-model.number="params.newsSentimentMinCoveragePct" class="num-input" min="0" max="100" step="1" />
            </div>
            <div v-if="params.newsSentimentFilter" class="param-row">
              <label class="control-label">Mode</label>
              <select v-model="params.newsSentimentMode" class="config-select">
                <option value="buy_only">Buy-only (block BUYs, let SELLs through)</option>
                <option value="both">Both (hard HOLD on either direction)</option>
              </select>
            </div>
            <label v-if="params.newsSentimentFilter" class="control-label checkbox-label">
              <input type="checkbox" v-model="params.newsSentimentUseZ" />
              Use 20d z-score instead of absolute
            </label>
            <div v-if="params.newsSentimentFilter && params.newsSentimentUseZ" class="param-row">
              <label class="control-label">z threshold</label>
              <input type="number" v-model.number="params.newsSentimentZThreshold" class="num-input" min="-3" max="3" step="0.1" />
            </div>
          </div>

          <div class="section-divider"></div>

          <!-- OTP Strategies -->
          <div class="subsection-header">OTP Strategies</div>
          <label class="control-label checkbox-label">
            <input type="checkbox" v-model="params.useHedging" /> Hedging
          </label>
          <!-- Hedge Config expands here when on -->
          <div v-if="params.useHedging && params.hedge" class="subsection inline-otp-config"
               data-testid="hedge-config">
            <div class="hedge-param-row">
              <label class="param-label">Trigger Type</label>
              <select v-model="params.hedge.triggerType" class="param-select">
                <option value="percentage">Percentage spike</option>
                <option value="delta">VIX delta (points)</option>
                <option value="absolute">VIX absolute level</option>
                <option value="regime">Regime transition</option>
              </select>
            </div>
            <div class="hedge-param-row" v-if="params.hedge.triggerType !== 'regime'">
              <label class="param-label">Trigger Threshold</label>
              <input type="number" v-model.number="params.hedge.triggerThreshold"
                     :min="0" :step="params.hedge.triggerType === 'delta' ? 0.5 : 1" class="param-input" />
              <span class="param-unit">{{ params.hedge.triggerType === 'percentage' ? '%' : params.hedge.triggerType === 'delta' ? 'pts' : '' }}</span>
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Hedge Size</label>
              <select v-model.number="params.hedge.hedgeSizeRatio" class="param-select">
                <option :value="0.58">Delta-neutral (0.58)</option>
                <option :value="0.75">Moderate (0.75)</option>
                <option :value="1.0">Full (1.0)</option>
              </select>
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Spread (bps)</label>
              <input type="number" v-model.number="params.hedge.spreadBps" :min="0" :step="5" class="param-input" />
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Annual TER</label>
              <input type="number" v-model.number="params.hedge.annualTer" :min="0" :max="0.05" :step="0.001" class="param-input" />
            </div>
            <div class="hedge-param-row">
              <label class="param-label">BGA Beta</label>
              <input type="number" v-model.number="params.hedge.bgaBeta" :min="0.5" :max="1.5" :step="0.05" class="param-input" />
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Dealing Charge</label>
              <input type="number" v-model.number="params.hedge.dealingChargeGbp" :min="0" :step="0.5" class="param-input" />
              <span class="param-unit">£</span>
            </div>
          </div>

          <label class="control-label checkbox-label">
            <input type="checkbox" v-model="params.useOverlay" /> Overlay
          </label>
          <div v-if="params.useOverlay && params.overlay" class="subsection inline-otp-config"
               data-testid="overlay-config">
            <div class="hedge-param-row">
              <label class="param-label">VIX Drop Trigger</label>
              <input type="number" v-model.number="params.overlay.triggerThreshold"
                     :max="0" :step="0.5" class="param-input" />
              <span class="param-unit">%</span>
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Deploy Size</label>
              <input type="number"
                     :value="Math.round(params.overlay.overlaySizePct * 100)"
                     @input="params.overlay.overlaySizePct = ($event.target as HTMLInputElement).valueAsNumber / 100"
                     :min="10" :max="100" :step="1" class="param-input" />
              <span class="param-unit">%</span>
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Spread (bps)</label>
              <input type="number" v-model.number="params.overlay.spreadBps" :min="0" :step="5" class="param-input" />
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Annual TER</label>
              <input type="number" v-model.number="params.overlay.annualTer" :min="0" :max="0.05" :step="0.001" class="param-input" />
            </div>
            <div class="hedge-param-row">
              <label class="param-label">Dealing Charge</label>
              <input type="number" v-model.number="params.overlay.dealingChargeGbp" :min="0" :step="0.5" class="param-input" />
              <span class="param-unit">£</span>
            </div>
          </div>

          <!-- Phase B (2026-09-06): Long Conviction / Short Conviction decommissioned.
               See docs/superpowers/plans/2026-09-06-otp-shadow-promotion-arc.md.
               Toggles retained (disabled) so historical presets that arm them
               remain visible on load, but new backtests cannot enable them —
               /api/backtest/run rejects submissions carrying either flag. -->
          <label class="control-label checkbox-label" data-testid="sc-decomm-label">
            <input type="checkbox" v-model="params.useShortConviction" disabled /> Short Conviction
            <span class="decomm-tag">decommissioned 2026-09-06</span>
          </label>
          <div v-if="params.useShortConviction" class="subsection inline-otp-config"
               data-testid="sc-config">
            <div class="param-note">Decommissioned — see the shadow-strategy presets in Phase C.4.</div>
          </div>

          <label class="control-label checkbox-label" data-testid="lc-decomm-label">
            <input type="checkbox" v-model="params.useLongConviction" disabled /> Long Conviction
            <span class="decomm-tag">decommissioned 2026-09-06</span>
          </label>
          <div v-if="params.useLongConviction" class="subsection inline-otp-config"
               data-testid="lc-config">
            <div class="param-note">Decommissioned — see the shadow-strategy presets in Phase C.4.</div>
          </div>

          <!-- OTP Reserve -->
          <div class="param-row">
            <label class="control-label">
              OTP Reserve
              <span class="info-icon" @click.stop="toggleTooltip('otpReserve')">i</span>
            </label>
            <select v-model="params.otpReservePct" class="num-input" data-testid="otp-reserve-select">
              <option :value="null">Sweep (auto)</option>
              <option :value="0">NO OTP</option>
              <option :value="0.10">10%</option>
              <option :value="0.15">15%</option>
              <option :value="0.20">20%</option>
              <option :value="0.25">25%</option>
              <option :value="0.33">33%</option>
              <option :value="0.50">50%</option>
            </select>
          </div>
          <div v-if="activeTooltip === 'otpReserve' && tooltips.otpReserve" class="tooltip-box">
            {{ tooltips.otpReserve }}
          </div>

          <div class="section-divider"></div>

          <!-- Calendar Filters (collapsed by default) -->
          <div class="advanced-toggle" @click="calendarExpanded = !calendarExpanded">
            <span class="advanced-chevron">{{ calendarExpanded ? '▾' : '▸' }}</span>
            Calendar Filters
          </div>
          <div v-if="calendarExpanded" class="subsection">
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.holidayFilter" /> Holiday Blackout (recommended)</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.futuresRollFilter" /> Futures Roll (recommended)</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.nfpFilter" /> NFP Blackout</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.opexFilter" /> OPEX Blackout</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.quadWitchingFilter" /> Quad Witching</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.exDividendFilter" /> Ex-Dividend Blackout</label>
            <label class="control-label checkbox-label"><input type="checkbox" v-model="params.eomInterestFilter" /> EOM Interest</label>
            <div v-if="params.eomInterestFilter" class="param-row">
              <label class="control-label">Boost (bps)</label>
              <input type="number" v-model.number="params.eomInterestBoostBps" class="num-input" min="0" max="100" />
            </div>
          </div>

          <div class="subsection" data-testid="synth-method-section">
            <label class="control-label">
              Synth NAV 09:45 method
              <span class="info-icon" @click.stop="toggleTooltip('synthMethod')">i</span>
            </label>
            <div v-if="activeTooltip === 'synthMethod'" class="tooltip-box">
              {{ tooltips.synthMethod }}
            </div>
            <select v-model="params.synthNav945Method" class="num-input" data-testid="synth-method-select">
              <option value="aggregate">aggregate (live writer primary)</option>
              <option value="etf_trick">etf_trick (K_t Method B)</option>
            </select>
          </div>

          <div class="subsection" data-testid="sampling-section">
            <label class="control-label">
              Sampling mode
              <span class="info-icon" @click.stop="toggleTooltip('sampling')">i</span>
            </label>
            <div v-if="activeTooltip === 'sampling'" class="tooltip-box">
              {{ tooltips.sampling }}
            </div>
            <select v-model="params.sampling" class="num-input" data-testid="sampling-select">
              <option value="daily">daily (every trading day)</option>
              <option value="event">event (CUSUM-event dates only)</option>
            </select>
            <div v-if="params.sampling === 'event'" class="param-row" style="margin-top: 0.5rem;">
              <small class="muted">
                CUSUM filter params (defaults match /api/cusum/bga-labels):
              </small>
            </div>
            <div v-if="params.sampling === 'event'" class="param-row">
              <label class="control-label">σ span</label>
              <input type="number" v-model.number="params.eventSamplingSigmaSpan" class="num-input" min="10" max="500" placeholder="100" />
              <label class="control-label">k_h</label>
              <input type="number" v-model.number="params.eventSamplingKH" class="num-input" min="0.5" max="5" step="0.1" placeholder="2.0" />
              <label class="control-label">horizon (d)</label>
              <input type="number" v-model.number="params.eventSamplingHorizonDays" class="num-input" min="1" max="60" placeholder="10" />
            </div>
            <div v-if="params.sampling === 'event'" class="param-row">
              <label class="control-label">
                Event NAV source
                <span class="info-icon" @click.stop="toggleTooltip('eventNavSource')">i</span>
              </label>
              <div v-if="activeTooltip === 'eventNavSource'" class="tooltip-box">
                {{ tooltips.eventNavSource }}
              </div>
              <select v-model="params.eventSamplingNavSource" class="num-input" data-testid="event-nav-source-select">
                <option value="official">official</option>
                <option value="kt_synth">kt_synth</option>
                <option value="fva_adjusted">fva_adjusted</option>
              </select>
            </div>
          </div>

          <div class="param-row">
            <label class="control-label">
              Decision driver
              <span class="info-icon" @click.stop="toggleTooltip('stepAside')">i</span>
            </label>
            <div v-if="activeTooltip === 'stepAside'" class="tooltip-box">
              {{ tooltips.stepAside }}
            </div>
            <!-- 2026-10-04: standalone Japan is always the pure 08:40 NIY sign rule (server-forced), so the
                 mode selector is replaced by a fixed label and the SELL/BUY band is always shown. -->
            <span v-if="isSignRuleOnly" class="control-label" data-testid="sign-rule-label">
              {{ signRuleCopy.rule }}
            </span>
            <select v-else v-model="params.stepAsideMode" class="num-input" data-testid="step-aside-mode-select">
              <option value="off">Synth-NAV strategy</option>
              <option value="pure">Hold unless futures down</option>
              <option value="gate">Synth-NAV strategy, futures can force SELL</option>
            </select>
            <template v-if="isSignRuleOnly || params.stepAsideMode !== 'off'">
              <label class="control-label">Futures drop to trigger SELL (bps)</label>
              <input type="number" v-model.number="params.stepAsideThresholdBps" class="num-input" min="-200" max="100" placeholder="0" data-testid="step-aside-threshold-input" />
              <!-- 2026-09-15: BUY-side threshold for the dead-zone rule.
                   Refused server-side and here when < SELL threshold. Default 0
                   = equal to SELL default = legacy one-sided (no dead zone). -->
              <label class="control-label" title="Empty = legacy one-sided rule (no dead zone). Any number opts in: prints in [SELL, BUY]bps hold current allocation. Must be >= SELL threshold.">Futures rise to trigger BUY (bps)</label>
              <input type="number" class="num-input" min="-200" max="200"
                     placeholder="(none = one-sided)"
                     :value="params.stepAsideBuyThresholdBps ?? ''"
                     @input="e => { const v = (e.target as HTMLInputElement).value; params.stepAsideBuyThresholdBps = v === '' ? null : Number(v) }"
                     data-testid="step-aside-buy-threshold-input" />
              <div v-if="stepAsideBandError" class="error-hint" data-testid="step-aside-band-error">{{ stepAsideBandError }}</div>
              <small v-if="isSignRuleOnly" class="hint" data-testid="band-hint">
                {{ signRuleCopy.hint }} One number in both boxes (or BUY empty)
                is today's one-sided rule: in the fund when the print is at or above it, cash below. BUY above SELL
                opens a dead zone where the position is held as is.
              </small>
              <!-- step_aside_reentry_bps retired 2026-09-02 (spec 2026-09-02-flicker-band-detector §5):
                   the engine ignores it, so the "re-BUY bar" input is gone. The field stays in the
                   TS type/defaults only so stored presets keep round-tripping. -->
            </template>
          </div>

        </div>
        <!-- end Hypothesis -->

        <!-- 2) Parameters -->
        <div class="section parameters-section">
          <div class="section-header">Parameters</div>

          <!-- Task 10 fix round 1: no auto-optimize for Japan (single run only). -->
          <label v-if="!isSignRuleOnly" class="control-label checkbox-label">
            <input type="checkbox" v-model="params.autoOptimize"
                   :disabled="validationMode === 'cpcv'"
                   data-testid="auto-opt-checkbox" />
            Auto-optimize swept parameters
            <span class="info-icon" @click.stop="toggleTooltip('autoOpt')">i</span>
          </label>
          <div v-if="activeTooltip === 'autoOpt'" class="tooltip-box">
            When enabled, the focused grid optimizer sweeps these parameters and selects the best combination.
          </div>

          <!-- Sweep extras (auto-opt on) -->
          <label class="control-label checkbox-label"
                 v-if="params.autoOptimize && (params.useOverlay || params.useShortConviction || params.useLongConviction || params.useHedging)">
            <input type="checkbox" v-model="sweepOverlaySc" />
            Sweep Overlay/SC/LC/Hedge
          </label>
          <div v-if="params.autoOptimize && sweepOverlaySc" style="display: flex; align-items: center; gap: 6px; margin-top: 4px;">
            <label class="control-label" style="margin: 0;">Budget:</label>
            <select v-model="overlaySweepBudget" class="config-select" style="width: auto;">
              <option value="quick">Quick (1m)</option>
              <option value="standard">Standard (15m)</option>
              <option value="deep">Deep (3h)</option>
              <option value="exhaustive">Exhaustive (6h+)</option>
            </select>
          </div>

          <!-- Auto-opt sweep summary (visible when auto-opt is on) -->
          <div v-if="params.autoOptimize" class="auto-opt-summary">
            {{ params.stepAsideMode === 'pure'
              ? 'Grid will sweep: Futures drop to SELL (0–50 bp) × rise to BUY (SELL + 0/5/10/20 bp) (36 combos)'
              : params.imrMode === 'momentum'
                ? 'Grid will sweep: IMR Window, Breadth Threshold, VIX Mom Gate (48 combos)'
                : 'Grid will sweep: Z Window, VIX Scale, VIX Mom Gate, Term Structure (36 combos)' }}
          </div>

          <!-- Pinned controls (auto-opt off) -->
          <template v-if="!params.autoOptimize">
            <template v-if="params.imrMode === 'momentum'">
              <label class="control-label">IMR Window</label>
              <select v-model.number="params.imrWindow" class="config-select" data-testid="pinned-imr-window">
                <option :value="3">3 days</option>
                <option :value="5">5 days</option>
                <option :value="7">7 days</option>
                <option :value="10">10 days</option>
                <option :value="20">20 days</option>
              </select>

              <div class="param-row">
                <label class="control-label">IMR Breadth Threshold</label>
                <input v-model.number="params.imrBreadthThreshold" type="number"
                       class="num-input" min="0" max="1" step="0.1"
                       data-testid="pinned-imr-breadth">
              </div>

              <label class="control-label">VIX Momentum Gate</label>
              <div class="radio-group" data-testid="pinned-vmg">
                <label class="radio-option"><input type="radio" v-model="params.vixMomGate" value="off" /> off</label>
                <label class="radio-option"><input type="radio" v-model="params.vixMomGate" value="regime" /> regime</label>
                <label class="radio-option"><input type="radio" v-model="params.vixMomGate" value="term" /> term</label>
                <label class="radio-option"><input type="radio" v-model="params.vixMomGate" value="both" /> both</label>
                <label class="radio-option"><input type="radio" v-model="params.vixMomGate" value="circuit" /> circuit</label>
              </div>
            </template>

            <template v-else>
              <!-- Classical pinned controls -->
              <div class="param-row">
                <div class="param-header">
                  <span class="param-name">Z-Score Window</span>
                  <span class="param-value">{{ params.zWindow }}d</span>
                </div>
                <input type="range" v-model.number="params.zWindow" :min="30" :max="250" :step="10"
                       data-testid="pinned-z-window" />
                <div class="range-labels"><span>30</span><span>250</span></div>
              </div>

              <div class="param-row">
                <div class="param-header">
                  <span class="param-name">MR Tilt</span>
                  <span class="param-value">{{ params.tiltBps }}bp</span>
                </div>
                <input type="range" v-model.number="params.tiltBps" :min="0" :max="50" :step="5" />
                <div class="range-labels"><span>0</span><span>50</span></div>
              </div>

              <div class="param-row">
                <div class="param-header">
                  <span class="param-name">VIX Threshold Scale</span>
                  <span class="param-value">{{ params.vixScale.toFixed(1) }}x</span>
                </div>
                <input type="range" v-model.number="params.vixScale" :min="0.5" :max="2.0" :step="0.1"
                       data-testid="pinned-vix-scale" />
                <div class="range-labels"><span>0.5</span><span>2.0</span></div>
              </div>

              <label class="control-label checkbox-label">
                <input type="checkbox" v-model="params.termStructure" /> Term Structure
              </label>
            </template>
          </template>

          <!-- IMR Tilt BPS (visible when imrMode in tilt/both, regardless of auto-opt) -->
          <div class="param-row" v-if="params.imrMode === 'tilt' || params.imrMode === 'both'">
            <label class="control-label">IMR Tilt BPS</label>
            <input v-model.number="params.imrTiltBps" type="number" class="num-input"
                   min="0" max="100" step="5" data-testid="imr-tilt-bps">
          </div>

        </div>
        <!-- end Parameters -->

        <!-- 3) Validation — sign-rule funds (Japan, Charteris) get None / CPCV only (2026-10-09); their
             walk-forward is the band optimisers'. -->
        <div class="section validation-section">
          <div class="section-header">Validation</div>
          <div class="radio-group radio-stack" data-testid="validation-radio">
            <label class="radio-option">
              <input type="radio" v-model="validationMode" value="none" />
              None
            </label>
            <label v-if="!isSignRuleOnly" class="radio-option">
              <input type="radio" v-model="validationMode" value="walk-forward" />
              Walk-Forward
            </label>
            <label class="radio-option">
              <input type="radio" v-model="validationMode" value="cpcv" />
              CPCV
            </label>
          </div>

          <template v-if="validationMode === 'walk-forward'">
            <div class="wf-row">
              <label class="wf-label">Train days</label>
              <input type="number" v-model.number="trainDays" class="num-input"
                     min="50" max="1000" data-testid="wf-train-days" />
            </div>
            <div class="wf-row">
              <label class="wf-label">Test days</label>
              <input type="number" v-model.number="testDays" class="num-input"
                     min="10" max="500" data-testid="wf-test-days" />
            </div>
            <div class="wf-row">
              <label class="wf-label">Fitness</label>
              <select v-model="fitnessPreset" class="num-input" style="width: auto;"
                      data-testid="wf-fitness">
                <option value="sharpe_only">Sharpe Only</option>
                <option value="balanced">Balanced</option>
                <option value="risk_adjusted">Risk Adjusted</option>
              </select>
            </div>
            <div class="wf-row">
              <label class="wf-label" title="Off: 24-combo grid (~30min-3h). On: 240-combo grid (~5-25h) — was the default until 2026-09-15 and produced a 25h runaway on gold.">
                <input type="checkbox" v-model="wfFullGrid" data-testid="wf-full-grid" />
                Full grid (slow: 240 combos, hours-to-a-day)
              </label>
            </div>
          </template>

          <template v-if="validationMode === 'cpcv'">
            <div class="advanced-toggle" @click="cpcvParamsExpanded = !cpcvParamsExpanded">
              <span class="advanced-chevron">{{ cpcvParamsExpanded ? '▾' : '▸' }}</span>
              CPCV Parameters
            </div>
            <div v-if="cpcvParamsExpanded" class="subsection">
              <div class="param-row">
                <label class="control-label">N</label>
                <input type="number" v-model.number="cpcvN" class="num-input"
                       min="2" max="20" data-testid="cpcv-n" />
              </div>
              <div class="param-row">
                <label class="control-label">k</label>
                <input type="number" v-model.number="cpcvK" class="num-input"
                       min="1" max="10" data-testid="cpcv-k" />
              </div>
              <div class="param-row">
                <label class="control-label">Embargo (days)</label>
                <input type="number" v-model.number="cpcvEmbargoDays" class="num-input"
                       min="0" max="60" data-testid="cpcv-embargo" />
              </div>
            </div>
          </template>
        </div>
        <!-- end Validation -->

        <!-- New primary run button (Task 3) + Advanced runs (Task 9) -->
        <div class="section">
          <!-- Task 10 fix round 1: Japan needs the JAPAN grant to run (server 404s otherwise). -->
          <div v-if="gatedRunBlocked" class="run-preview" data-testid="run-requires-grant">
            Running a {{ signRuleCopy.fund }} backtest requires the {{ signRuleCopy.grant }} grant — view only.
          </div>
          <div v-else class="run-row">
            <button
              class="btn-run primary-run-btn"
              data-testid="run-backtest"
              @click="runPrimary"
              :disabled="isJobRunning || loading">
              {{ isJobRunning ? 'Running...' : primaryRunLabel }}
            </button>
            <!-- 2026-10-05 (Paolo): Japan's optimiser — sweeps only the SELL/BUY band (36 combos, pure
                 sign rule per combo, server JapanThresholdSweep); the winner carries a DSR deflated by 36. -->
            <button
              v-if="isSignRuleOnly"
              class="btn-run"
              data-testid="japan-optimise-btn"
              @click="runOptimize"
              :disabled="isJobRunning || loading"
              title="Runs 36 backtests: SELL 0-50 bp x BUY = SELL + 0/5/10/20 bp, ranked by full-period Sharpe. The DSR deflates the winner for those 36 tries.">
              Optimise SELL/BUY band (36 runs · DSR)
            </button>
            <button
              v-if="isSignRuleOnly"
              class="btn-run"
              data-testid="band-wf-btn"
              @click="runBandWalkForward"
              :disabled="isJobRunning || loading"
              title="Walk-forward check of the same 36 bands (250-day train / 60-day test folds): every band gets its full-period result plus stitched out-of-sample Sharpe/CAGR, and the sensitivity heatmap. Slower (about an hour).">
              Walk-forward band check (36 bands · OOS)
            </button>
            <button
              v-if="isJobRunning"
              class="btn-cancel"
              data-testid="cancel-backtest"
              @click="cancelBacktest"
              title="Cancels a queued backtest. If it has already started running, the cancel is a no-op (running-job interruption not yet wired).">
              Cancel
            </button>
          </div>
          <div class="run-preview" data-testid="run-preview">{{ runPreview }}</div>
          <!-- Signal-stack preview (2026-07-19): the source fields are hidden
               pass-throughs; three 07-17/18 incidents ran sweeps on a stale
               stack the operator could not see. Amber = a combination that is
               almost never what a BGA run intends. -->
          <div class="run-preview stack-preview" :class="{ 'stack-warn': stackWarning }" data-testid="run-stack-preview">
            stack: {{ stackPreview }}
          </div>

          <!-- Task 10 fix round 1: no Advanced runs (TPE/Schedule) for Japan — single run only. -->
          <div v-if="!isSignRuleOnly" class="advanced-toggle" @click="advancedRunsExpanded = !advancedRunsExpanded"
               data-testid="advanced-runs-toggle">
            <span class="advanced-chevron">{{ advancedRunsExpanded ? '▾' : '▸' }}</span>
            Advanced runs
          </div>
          <div v-if="advancedRunsExpanded && !isSignRuleOnly" class="advanced-runs-body">
            <div class="advanced-run-row">
              <button class="btn-run btn-tpe"
                      @click="runFullOptimize"
                      :disabled="isJobRunning || loading"
                      data-testid="run-tpe-btn">
                {{ isJobRunning ? 'Running...' : `Full Optimize (TPE ${tpeTrialBudget})` }}
              </button>
              <input type="number" v-model.number="tpeTrialBudget" class="text-input"
                     style="width: 60px; margin-left: 4px;" min="20" max="2000" step="10"
                     title="TPE trial budget" />
            </div>
            <!-- Task 10 (F6): no standalone Japan schedule — the /schedule whitelist excludes it. -->
            <div v-if="!isSignRuleOnly" class="advanced-run-row">
              <button class="btn-schedule" @click="showScheduleInput = !showScheduleInput"
                      data-testid="schedule-btn">
                Schedule
              </button>
            </div>
            <div v-if="showScheduleInput && !isSignRuleOnly" class="schedule-row">
              <input v-model="cronExpression" class="text-input" placeholder="0 6 * * 1-5"
                     @keyup.enter="scheduleBacktest" />
              <button class="btn-action" @click="scheduleBacktest">Set</button>
            </div>
          </div>
        </div>

        <!-- Error display -->
        <div v-if="error" class="error-box">{{ error }}</div>

        <!-- Progress bar -->
        <div v-if="isJobRunning" class="progress-box">
          <div class="progress-header">
            <span>{{ progressPhase }}</span>
            <span>{{ progressFolds }}</span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
          </div>
          <div class="progress-eta">{{ progressPct.toFixed(0) }}% complete</div>
        </div>
      </div>
    </div>

    <!-- Results Panel (right) -->
    <div class="results-panel">
      <div v-if="recentRuns.length" class="recent-runs">
        <label class="control-label" for="recent-runs-picker">Recent runs</label>
        <select id="recent-runs-picker" v-model="pickedRunId" class="num-input" data-testid="recent-runs-picker"
                @change="openRecentRun(pickedRunId)">
          <option value="">— open a past run —</option>
          <option v-for="j in recentRuns" :key="j.jobId" :value="j.jobId">{{ recentRunLabel(j) }}</option>
        </select>
      </div>

      <!-- No results placeholder -->
      <div v-if="!activeJob || (!activeJob.result && !activeJob.walkForwardResult && !activeJob.cpcvResult && !isJobRunning)" class="no-results">
        <div>{{ selectedConfig ? 'No linked results \u2014 run a backtest to generate' : 'Run a backtest to see results' }}</div>
        <div v-if="selectedConfig && linkedJobMissing && !gatedRunBlocked" style="margin-top: 12px;">
          <button
            class="btn-action"
            data-testid="relink-preset-button"
            :disabled="relinking"
            @click="runAndLinkSelectedPreset">
            {{ relinking ? 'Running\u2026' : 'Run backtest to link' }}
          </button>
          <div v-if="relinkError" class="relink-error" data-testid="relink-error"
               style="color: var(--sw-danger, #c05); margin-top: 8px; font-size: 12px;">
            {{ relinkError }}
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-else-if="isJobRunning && !currentResult && !activeJob?.cpcvResult" class="no-results">
        Backtest running... {{ progressPct.toFixed(0) }}%
      </div>

      <!-- Results -->
      <template v-else>
        <!-- Results action bar (Task 11) -->
        <div v-if="loadedRow && loadedRow.jobId === activeJob?.jobId" class="loaded-row-banner" data-testid="loaded-row-banner">
          Showing grid row #{{ loadedRow.rank }}
          <template v-if="loadedRow.params.stepAsideMode === 'pure'">
            (SELL {{ loadedRow.params.stepAsideThresholdBps }} bp / BUY {{ loadedRow.params.stepAsideBuyThresholdBps ?? loadedRow.params.stepAsideThresholdBps }} bp)
          </template>
          — full-period result.
          <button class="btn-sm" data-testid="loaded-row-back" @click="backToGridWinner">Back to grid winner</button>
        </div>
        <div v-if="activeJob?.result || activeJob?.walkForwardResult || activeJob?.optimizeResult || activeJob?.cpcvResult"
             class="results-action-bar"
             data-testid="results-action-bar">
          <button class="btn-action"
                  @click="promptSaveConfigFromResults"
                  :disabled="loading || !canSaveFromResults"
                  :title="saveFromResultsLabel"
                  data-testid="save-config-btn">
            {{ canSaveFromResults ? 'Save as Config' : 'Save as Config (form ≠ result)' }}
          </button>
          <!-- 2026-10-04: JAPAN promotes too — it writes config:mr:japan, the live 08:40 NIY threshold. -->
          <button class="btn-action btn-warn"
                  @click="applyToLiveTrading"
                  :disabled="loading || !canApplyToLive"
                  :title="applyToLiveLabel"
                  data-testid="apply-to-live-btn">
            {{ applyToLiveLabel }}
          </button>
        </div>

        <!-- Strategy name banner -->
        <div v-if="strategyLabel" class="strategy-banner">{{ strategyLabel }}</div>

        <!-- Run Summary header -->
        <div v-if="activeJob?.result || activeJob?.walkForwardResult || activeJob?.optimizeResult || activeJob?.cpcvResult" class="run-summary">
          <div class="run-summary-top">
            <span class="run-type">{{ runTypeLabel }}</span>
            <span v-if="activeJob?.fitnessPreset" class="run-preset">— {{ activeJob.fitnessPreset }}</span>
          </div>
          <div class="run-summary-meta">
            {{ runSummaryMeta }}
          </div>
          <div v-if="runWinnerSummary" class="run-summary-winner">
            Winner: {{ runWinnerSummary }}
          </div>
        </div>

        <!-- KPI cards -->
        <div v-if="currentResult" class="kpi-grid">
          <div class="kpi">
            <div class="kpi-label">CAGR</div>
            <div class="kpi-value" :class="valueClass(currentResult.cagrPct)">
              {{ fmtPct(currentResult.cagrPct) }}
            </div>
          </div>
          <div class="kpi">
            <div class="kpi-label">Sharpe</div>
            <div class="kpi-value" :class="currentResult.sharpeRatio > 1 ? 'positive' : ''">
              {{ fmtSharpe(currentResult.sharpeRatio) }}
            </div>
          </div>
          <div class="kpi">
            <div class="kpi-label">Max DD</div>
            <div class="kpi-value negative">
              {{ fmtPct(currentResult.maxDrawdownPct) }}
            </div>
          </div>
          <div v-if="winnerDsr !== null" class="kpi">
            <div class="kpi-label">DSR</div>
            <div class="kpi-value" :style="{ color: dsrColour(winnerDsr) }">
              {{ (winnerDsr >= 0 ? '+' : '') + winnerDsr.toFixed(2) }}
            </div>
          </div>
          <div v-else class="kpi">
            <div class="kpi-label">vs B&amp;H</div>
            <div class="kpi-value" :class="valueClass(currentResult.excessReturnPct)">
              {{ fmtPct(currentResult.excessReturnPct) }}
            </div>
          </div>
        </div>

        <!-- Additional stats row -->
        <div v-if="currentResult" class="stats-row">
          <span class="stat-item" v-if="currentResult.cycleCount > 0">
            <span class="stat-label">Win Rate (cycle):</span>
            {{ fmtPct(currentResult.cycleWinRate) }}
            <span style="color: #888; font-size: 0.9em;">({{ currentResult.cycleCount }} cycles)</span>
          </span>
          <span class="stat-item">
            <span class="stat-label">Win Rate (day):</span>
            {{ fmtPct(currentResult.winRate) }}
          </span>
          <span class="stat-item">
            <span class="stat-label">Trades:</span>
            {{ currentResult.tradeCount }}
          </span>
          <span class="stat-item">
            <span class="stat-label">Days:</span>
            {{ currentResult.tradingDays }}
          </span>
          <span class="stat-item">
            <span class="stat-label">Period:</span>
            {{ currentResult.startDate?.slice(0, 10) }} - {{ currentResult.endDate?.slice(0, 10) }}
          </span>
        </div>

        <!-- Hedge Summary (annualised to match CAGR period) -->
        <div v-if="currentResult && currentResult.hedgeDaysCount > 0" class="hedge-summary">
          <div class="section-label" style="margin-top: 0.5rem;">HEDGE SUMMARY (per annum)</div>
          <div class="kpi-label" style="margin-bottom:4px;">
            Deploy Size: {{ params.hedge
                            ? ((params.hedge.cashBufferPct * params.hedge.hedgeSizeRatio) * 100).toFixed(1) + '%'
                            : '—' }}
          </div>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">Hedge Days / yr</div>
              <div class="kpi-value">{{ Math.round(currentResult.hedgeDaysCount / (currentResult.tradingDays / 252)) }}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Win Rate</div>
              <div class="kpi-value" :class="currentResult.hedgeWinRate >= 0.5 ? 'positive' : 'negative'">
                {{ (currentResult.hedgeWinRate * 100).toFixed(0) }}%
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Hedge P&amp;L / yr</div>
              <div class="kpi-value" :class="currentResult.hedgeTotalPnl >= 0 ? 'positive' : 'negative'">
                {{ currentResult.hedgeTotalPnl >= 0 ? '+' : '' }}£{{ Math.abs(Math.round(currentResult.hedgeTotalPnl / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Hedge Costs / yr</div>
              <div class="kpi-value">£{{ Math.abs(Math.round(currentResult.hedgeTotalCosts / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}</div>
            </div>
          </div>
        </div>

        <!-- Overlay Summary (annualised to match CAGR period) -->
        <div v-if="currentResult && currentResult.overlayDaysCount > 0" class="hedge-summary">
          <div class="section-label" style="margin-top: 0.5rem;">OVERLAY SUMMARY (per annum)</div>
          <div class="kpi-label" style="margin-bottom:4px;">
            Deploy Size: {{ params.overlay
                            ? (params.overlay.overlaySizePct * 100).toFixed(1) + '%'
                            : '—' }}
          </div>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">Overlay Days / yr</div>
              <div class="kpi-value">{{ Math.round(currentResult.overlayDaysCount / (currentResult.tradingDays / 252)) }}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Win Rate</div>
              <div class="kpi-value" :class="currentResult.overlayWinRate >= 0.5 ? 'positive' : 'negative'">
                {{ (currentResult.overlayWinRate * 100).toFixed(0) }}%
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Overlay P&amp;L / yr</div>
              <div class="kpi-value" :class="currentResult.overlayTotalPnl >= 0 ? 'positive' : 'negative'">
                {{ currentResult.overlayTotalPnl >= 0 ? '+' : '' }}&pound;{{ Math.abs(Math.round(currentResult.overlayTotalPnl / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Overlay Costs / yr</div>
              <div class="kpi-value">&pound;{{ Math.abs(Math.round(currentResult.overlayTotalCosts / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}</div>
            </div>
          </div>
        </div>

        <!-- Short Conviction Summary (annualised to match CAGR period) -->
        <div v-if="currentResult && currentResult.shortConvictionDaysCount > 0" class="hedge-summary">
          <div class="section-label" style="margin-top: 0.5rem;">SHORT CONVICTION SUMMARY (per annum)</div>
          <div class="kpi-label" style="margin-bottom:4px;">
            Deploy Size: {{ params.shortConviction
                            ? ((params.shortConviction.cashBufferPct * params.shortConviction.hedgeSizeRatio) * 100).toFixed(1) + '%'
                            : '—' }}
          </div>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">SC Days / yr</div>
              <div class="kpi-value">{{ Math.round(currentResult.shortConvictionDaysCount / (currentResult.tradingDays / 252)) }}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Win Rate</div>
              <div class="kpi-value" :class="currentResult.shortConvictionWinRate >= 0.5 ? 'positive' : 'negative'">
                {{ (currentResult.shortConvictionWinRate * 100).toFixed(0) }}%
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">SC P&amp;L / yr</div>
              <div class="kpi-value" :class="currentResult.shortConvictionTotalPnl >= 0 ? 'positive' : 'negative'">
                {{ currentResult.shortConvictionTotalPnl >= 0 ? '+' : '' }}&pound;{{ Math.abs(Math.round(currentResult.shortConvictionTotalPnl / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">SC Costs / yr</div>
              <div class="kpi-value">&pound;{{ Math.abs(Math.round(currentResult.shortConvictionTotalCosts / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}</div>
            </div>
          </div>
        </div>

        <!-- Long Conviction Summary (annualised to match CAGR period) -->
        <div v-if="currentResult && currentResult.longConvictionDaysCount > 0" class="hedge-summary">
          <div class="section-label" style="margin-top: 0.5rem;">LONG CONVICTION SUMMARY (per annum)</div>
          <div class="kpi-label" style="margin-bottom:4px;">
            Deploy Size: {{ params.longConviction
                            ? (params.longConviction.overlaySizePct * 100).toFixed(1) + '%'
                            : '—' }}
          </div>
          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">LC Days / yr</div>
              <div class="kpi-value">{{ Math.round(currentResult.longConvictionDaysCount / (currentResult.tradingDays / 252)) }}</div>
            </div>
            <div class="kpi">
              <div class="kpi-label">Win Rate</div>
              <div class="kpi-value" :class="currentResult.longConvictionWinRate >= 0.5 ? 'positive' : 'negative'">
                {{ (currentResult.longConvictionWinRate * 100).toFixed(0) }}%
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">LC P&amp;L / yr</div>
              <div class="kpi-value" :class="currentResult.longConvictionTotalPnl >= 0 ? 'positive' : 'negative'">
                {{ currentResult.longConvictionTotalPnl >= 0 ? '+' : '' }}&pound;{{ Math.abs(Math.round(currentResult.longConvictionTotalPnl / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label">LC Costs / yr</div>
              <div class="kpi-value">&pound;{{ Math.abs(Math.round(currentResult.longConvictionTotalCosts / (currentResult.tradingDays / 252))).toLocaleString('en-GB') }}</div>
            </div>
          </div>
        </div>

        <!-- Equity curve chart -->
        <div v-if="equityCurvePoints" class="equity-chart">
          <div class="section-label">Equity Curve</div>
          <svg :viewBox="equityCurvePoints.viewBox" class="equity-svg" preserveAspectRatio="xMidYMid meet">
            <g v-html="equityCurvePoints.grid"></g>
            <!-- B&H line (dashed grey, behind strategy) -->
            <polyline
              v-if="equityCurvePoints.hasBh"
              :points="equityCurvePoints.bhPolyline"
              fill="none"
              stroke="#666"
              stroke-width="1"
              stroke-dasharray="4,3"
              stroke-linejoin="round"
            />
            <!-- Unhedged line (amber, when hedging active) -->
            <polyline
              v-if="equityCurvePoints.hasUnhedged"
              :points="equityCurvePoints.unhedgedPolyline"
              fill="none"
              stroke="#ff9800"
              stroke-width="1"
              stroke-dasharray="6,3"
              stroke-linejoin="round"
            />
            <!-- BGA contribution line (yellow solid, when OTPs active) -->
            <polyline
              v-if="equityCurvePoints.hasBgaContrib"
              :points="equityCurvePoints.bgaContribPolyline"
              fill="none"
              stroke="#facc15"
              stroke-width="1"
              stroke-linejoin="round"
            />
            <!-- Strategy line (solid green, on top) -->
            <polyline
              :points="equityCurvePoints.polyline"
              fill="none"
              stroke="#4caf50"
              stroke-width="1.5"
              stroke-linejoin="round"
            />
            <g v-html="equityCurvePoints.yLabels"></g>
            <g v-html="equityCurvePoints.xLabels"></g>
            <g v-html="equityCurvePoints.legend"></g>
          </svg>
        </div>
        <div v-else-if="currentResult" class="equity-placeholder">
          No equity curve data available
        </div>

        <!-- In-sample vs Walk-forward (refit) comparison bar -->
        <div v-if="hasWalkForward && wfResult && (wfResult.inSampleResult?.equityCurve?.length || wfResult.refitResult?.equityCurve?.length)" class="comparison-bar">
          <div class="comparison-side">
            <div class="comparison-label">IN-SAMPLE</div>
            <div class="comparison-metrics">
              <span>CAGR {{ fmtPct(wfResult.inSampleResult?.cagrPct) }}</span>
              <span>Sharpe {{ fmtSharpe(wfResult.inSampleResult?.sharpeRatio) }}</span>
            </div>
          </div>
          <div class="comparison-side">
            <div class="comparison-label">OUT-OF-SAMPLE (REFIT)</div>
            <div class="comparison-metrics">
              <span>CAGR {{ fmtPct(wfResult.refitResult?.cagrPct) }}</span>
              <span>Sharpe {{ fmtSharpe(wfResult.refitResult?.sharpeRatio) }}</span>
            </div>
            <div class="decay-row">
              <span class="decay-label">CAGR Decay:</span>
              <span :class="decayClass(wfResult.cagrDecayRefit)">{{ fmtPct(wfResult.cagrDecayRefit) }}</span>
              <span class="decay-label" style="margin-left: 8px;">Sharpe Decay:</span>
              <span :class="decayClass(wfResult.sharpeDecayRefit)">{{ fmtPct(wfResult.sharpeDecayRefit) }}</span>
            </div>
          </div>
        </div>

        <!-- Walk-forward fold table (only if we have fold RESULTS, not just fold periods) -->
        <div v-if="hasFoldResults" class="fold-section">
          <div class="section-label">Walk-Forward Folds</div>
          <table class="fold-table">
            <thead>
              <tr>
                <th>Fold</th>
                <th>Test Period</th>
                <th>Return</th>
                <th>Sharpe</th>
                <th>Train Sharpe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fold in currentFolds" :key="fold.fold">
                <td>{{ fold.fold }}</td>
                <td>{{ fold.testStart?.slice(0, 10) }} - {{ fold.testEnd?.slice(0, 10) }}</td>
                <td :class="valueClass(fold.result?.totalReturnPct)">
                  {{ fmtPct(fold.result?.totalReturnPct) }}
                </td>
                <td :class="fold.result?.sharpeRatio != null && fold.result.sharpeRatio > 1 ? 'positive' : ''">
                  {{ fmtSharpe(fold.result?.sharpeRatio) }}
                </td>
                <td>{{ fmtSharpe(fold.trainSharpe) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Optimizer results -->
        <template v-if="activeJob?.optimizeResult && !activeJob?.walkForwardResult?.fixedGridResult && optimizeResult">
          <div class="opt-summary">
            Grid: {{ optimizeResult.totalCombos.toLocaleString() }} combos in {{ (optimizeResult.elapsedMs / 1000).toFixed(1) }}s
          </div>
          <OptimizerResultsTable :topResults="optimizeResult.topResults" :scoreType="optimizeResult.scoreType" :loadedRank="loadedRow?.jobId === activeJob?.jobId ? loadedRow?.rank ?? null : null" @load="loadOptimizerRow" />
          <ParamHeatmap :allScores="optimizeResult.allScores" :scoreType="optimizeResult.scoreType" />
        </template>

        <template v-if="activeJob?.walkForwardResult?.fixedGridResult && wfOptimizeResult?.fixedGridResult">
          <WalkForwardChart
            v-if="wfOptimizeResult.folds?.some(f => f.trainScore || f.testScore)"
            :folds="wfOptimizeResult.folds"
            :meanOosScore="wfOptimizeResult.meanOosScore ?? 0"
            :stdOosScore="wfOptimizeResult.stdOosScore ?? 0"
            :scoreType="wfOptimizeResult.scoreType ?? 'sharpe'"
          />
          <div class="opt-summary">
            Fixed Grid: {{ wfOptimizeResult.fixedGridResult.totalCombos.toLocaleString() }} combos in {{ (wfOptimizeResult.fixedGridResult.elapsedMs / 1000).toFixed(1) }}s
          </div>
          <!-- 2026-10-08: the honest walk-forward number — what you'd have got by re-choosing on each training window. -->
          <div v-if="wfOptimizeResult.fixedGridResult.selectedOosSharpe != null" class="opt-summary" data-testid="wf-selected-oos"
               title="Each fold picks the combo with the best training fitness, then only that combo's next test window counts; the windows are stitched together. The OOS columns in the table are each fixed combo on every test window.">
            Pick-then-test (train winner each fold, judged on its next {{ activeJob?.testDays ?? 60 }} days):
            Sharpe {{ wfOptimizeResult.fixedGridResult.selectedOosSharpe.toFixed(2) }} ·
            CAGR {{ ((wfOptimizeResult.fixedGridResult.selectedOosCagr ?? 0) * 100).toFixed(1) }}% ·
            {{ wfOptimizeResult.fixedGridResult.selectedOosDays }} days ·
            folds picked {{ wfOptimizeResult.fixedGridResult.selectedOosDistinctPicks }} different combo(s)
          </div>
          <OptimizerResultsTable :topResults="wfOptimizeResult.fixedGridResult.topResults" :scoreType="wfOptimizeResult.fixedGridResult.scoreType" :loadedRank="loadedRow?.jobId === activeJob?.jobId ? loadedRow?.rank ?? null : null" @load="loadOptimizerRow" />
          <ParamHeatmap :allScores="wfOptimizeResult.fixedGridResult.allScores" :scoreType="wfOptimizeResult.fixedGridResult.scoreType" />
        </template>

        <!-- CPCV results -->
        <div v-if="activeJob?.cpcvResult" class="cpcv-results">
          <!-- Summary card -->
          <div class="cpcv-summary">
            <h3>CPCV Result</h3>
            <div class="summary-grid">
              <div><span class="label">N:</span> {{ activeJob.cpcvResult.n }}</div>
              <div><span class="label">k:</span> {{ activeJob.cpcvResult.k }}</div>
              <div><span class="label">Embargo:</span> {{ activeJob.cpcvResult.embargoDays }} days</div>
              <div><span class="label">Combinations:</span> {{ activeJob.cpcvResult.numCombinations }}</div>
              <div><span class="label">Paths:</span> {{ activeJob.cpcvResult.numPaths }}</div>
              <div><span class="label">Elapsed:</span> {{ (activeJob.cpcvResult.elapsedMs / 1000).toFixed(1) }}s</div>
            </div>
          </div>

          <!-- Fan chart (shared component, also used by FundBacktestPanel) -->
          <CpcvPathsChart :cpcvResult="activeJob.cpcvResult" />

          <!-- Distribution table -->
          <div class="cpcv-distribution">
            <h4>Distribution Across Paths</h4>
            <table class="metrics-table">
              <thead>
                <tr><th>Metric</th><th>Mean</th><th>Median</th><th>StdDev</th><th>Min</th><th>Max</th><th>P5</th><th>P95</th></tr>
              </thead>
              <tbody>
                <tr v-for="(s, name) in distributionRows" :key="name">
                  <td>{{ name }}</td>
                  <td>{{ s.mean.toFixed(3) }}</td>
                  <td>{{ s.median.toFixed(3) }}</td>
                  <td :style="{ color: stdColour(s.stdDev, s.mean) }">{{ s.stdDev.toFixed(3) }}</td>
                  <td>{{ s.min.toFixed(3) }}</td>
                  <td>{{ s.max.toFixed(3) }}</td>
                  <td>{{ s.p5.toFixed(3) }}</td>
                  <td>{{ s.p95.toFixed(3) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Per-path table -->
          <div class="cpcv-paths">
            <h4>Per-Path Metrics</h4>
            <table class="metrics-table">
              <thead>
                <tr><th>Path</th><th>Sharpe</th><th>Sortino</th><th>CAGR</th><th>MaxDD</th><th>PF</th><th>WinRate</th><th>Trades</th><th>Fitness</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in activeJob.cpcvResult.paths" :key="p.pathId">
                  <td>
                    <span class="dot" :style="{ background: pathConsistencyColour(p.sharpeRatio) }"></span>
                    {{ p.pathId }}
                  </td>
                  <td>{{ p.sharpeRatio.toFixed(3) }}</td>
                  <td>{{ p.sortinoRatio.toFixed(3) }}</td>
                  <td>{{ (p.cagrPct * 100).toFixed(1) }}%</td>
                  <td>{{ (p.maxDrawdownPct * 100).toFixed(1) }}%</td>
                  <td>{{ p.profitFactor.toFixed(2) }}</td>
                  <td>{{ (p.winRate * 100).toFixed(1) }}%</td>
                  <td>{{ p.tradeCount }}</td>
                  <td :style="{ color: p.fitness != null && p.fitness <= -1e20 ? '#f44336' : undefined }">{{ p.fitness != null ? (p.fitness <= -1e20 ? 'FAIL' : p.fitness.toFixed(3)) : '--' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <!-- TPE Full Optimize results -->
        <template v-if="tpeResult">
          <div class="opt-summary">
            TPE: {{ tpeResult.totalTrials }} trials across {{ tpeResult.groups.length }} strategy families
            in {{ (tpeResult.elapsedMs / 1000).toFixed(1) }}s
          </div>
          <div class="tpe-groups">
            <table class="mit-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Strategy</th>
                  <th>Sharpe</th>
                  <th>CAGR</th>
                  <th>Max DD</th>
                  <th>Trades</th>
                  <th>DSR</th>
                  <th>Trials</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <!--
                  Every metric column prefers fullResult when the detail pass
                  has run for this group (top 10 only). Those are the
                  authoritative full-BacktestEngine numbers (with subledger
                  costs, FX, HL platform fee). The g.* fields come from
                  LightweightBacktestRunner which skips all of that and will
                  always report a higher CAGR than the real engine. DSR on
                  fullResult is patched in by RunFullOptimizeAsync using the
                  TPE trial budget as numTrials so it's properly deflated.
                -->
                <tr
                  v-for="(g, idx) in tpeResult.groups"
                  :key="g.groupKey"
                  class="tpe-group-row"
                  :class="{ 'tpe-winner': idx === 0 }"
                >
                  <td>{{ idx === 0 ? '\u2605' : '' }}</td>
                  <td>{{ g.label }}</td>
                  <td>{{ ((g.fullResult?.sharpeRatio ?? g.sharpe) ?? 0).toFixed(2) }}</td>
                  <td>{{ (((g.fullResult?.cagrPct ?? g.cagr) ?? 0) * 100).toFixed(1) }}%</td>
                  <td>{{ (((g.fullResult?.maxDrawdownPct ?? g.maxDrawdownPct) ?? 0) * 100).toFixed(1) }}%</td>
                  <td>{{ g.fullResult?.tradeCount ?? g.tradeCount ?? 0 }}</td>
                  <td>{{ ((g.fullResult?.deflatedSharpeRatio ?? g.deflatedSharpe) ?? 0).toFixed(2) }}</td>
                  <td class="pill-dim">{{ g.trialCount }}</td>
                  <td>
                    <button class="btn-sm" @click="loadTpeWinner(g)"
                      :disabled="tpeGroupBackfilling !== null"
                      :title="!g.fullResult ? 'Group is outside the top-10 detail pass — clicking Load will run a fresh single-mode backtest with these params (~15-30s) so the KPI tiles and equity curve reflect this group.' : ''">
                      {{ tpeGroupBackfilling?.label === g.label ? 'Running…' : 'Load' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Live trading confirmation banner -->
        <div v-if="liveAppliedBanner" class="live-applied-banner">{{ liveAppliedBanner }}</div>

      </template>
    </div>
    </template>
  </section>
</template>

<style scoped>
.backtest-panel {
  display: flex;
  gap: 16px;
}
.decomm-tag {
  display: inline-block; margin-left: 8px;
  font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase;
  color: #b8a768; background: #2a2210; border: 1px solid #6b5a1f;
  border-radius: 3px; padding: 1px 6px;
}

.auth-state-msg {
  padding: 24px;
  color: #aaa;
  font-size: 14px;
  text-align: center;
  width: 100%;
}
.auth-state-msg small {
  display: inline-block;
  margin-top: 8px;
  color: #888;
}

/* ---- Controls Panel ---- */

.controls-panel {
  width: 280px;
  flex-shrink: 0;
  border-right: 1px solid #333;
  padding-right: 16px;
}

.controls-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  cursor: pointer;
}

.controls-title {
  font-size: 13px;
  font-weight: bold;
  color: #e0e0e0;
}

.config-summary {
  font-size: 10px;
  color: #888;
  display: none;
}

.collapse-icon {
  font-size: 16px;
  color: #888;
  font-weight: bold;
  width: 20px;
  text-align: center;
}

.section-label {
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  margin: 10px 0 6px;
  letter-spacing: 0.5px;
}

.select-input {
  width: 100%;
  padding: 6px 8px;
  background: #222;
  color: #e0e0e0;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 12px;
  min-height: 28px;
}

.section {
  margin-bottom: 12px;
}
.section-header {
  font-size: 10px;
  color: #666;
  text-transform: uppercase;
  margin: 10px 0 6px;
  letter-spacing: 0.5px;
}
.config-row { margin-bottom: 6px; }
.config-select { width: 100%; background: #1a1a1a; border: 1px solid #333; color: #e0e0e0; padding: 6px 8px; border-radius: 4px; font-size: 12px; }
.config-actions { display: flex; gap: 6px; }
.btn-sm { background: #333; border: 1px solid #444; color: #ccc; padding: 4px 10px; border-radius: 4px; font-size: 11px; cursor: pointer; flex: 1; }
.btn-sm:hover { background: #444; }
.btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-sm.btn-danger { background: #3a1a1a; border-color: #5a2a2a; color: #f88; }
.btn-sm.btn-warn { background: #3a2a10; border-color: #5a4a20; color: #fb9; }
.config-save-row { display: flex; gap: 6px; margin-top: 6px; }
.config-save-row .text-input { flex: 1; }

.save-row {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.text-input {
  flex: 1;
  padding: 6px 8px;
  background: #222;
  color: #e0e0e0;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 12px;
  min-height: 28px;
}

/* ---- Strategy section controls ---- */

.control-label { display: block; font-size: 12px; color: #ccc; margin: 8px 0 4px; }
.radio-group { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
.radio-option { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #ccc; cursor: pointer; }
.radio-option input { margin: 0; }
.section-divider { border-top: 1px solid #333; margin: 10px 0 8px 0; }
.checkbox-label { display: flex; align-items: center; gap: 6px; }
.checkbox-label input { margin: 0; }
.auto-opt-summary {
  font-size: 0.75rem;
  color: #888;
  padding: 4px 0;
  font-style: italic;
}
.strategy-param-row { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.strategy-param-row .control-label { flex: 1; margin: 0; }
.slider { flex: 2; }
.slider-value { min-width: 40px; font-size: 11px; color: #ccc; text-align: right; }

/* ---- Parameter sliders ---- */

.param-row {
  margin-bottom: 10px;
}

.param-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
}

.param-name {
  color: #aaa;
  font-size: 12px;
}

.param-value {
  color: #4caf50;
  font-size: 12px;
  font-family: monospace;
}

input[type="range"] {
  width: 100%;
  accent-color: #4caf50;
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #555;
}

/* ---- Info tooltips ---- */

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #333;
  border: 1px solid #555;
  color: #aaa;
  font-size: 10px;
  font-style: italic;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;
  vertical-align: middle;
  user-select: none;
  transition: all 0.15s;
  font-family: Georgia, serif;
}
.info-icon:hover {
  background: #444;
  color: #4caf50;
  border-color: #4caf50;
}

.tooltip-box {
  background: #1a2a1a;
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 11px;
  line-height: 1.5;
  color: #ccc;
  margin: 4px 0 8px;
  animation: tooltipFadeIn 0.15s ease;
}
@keyframes tooltipFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ---- Filter checkboxes ---- */

.filter-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
}

.rec-badge {
  font-size: 0.65rem;
  background: #1b3a1b;
  color: #3fb950;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.advanced-note {
  font-size: 0.7rem;
  color: #484f58;
  font-style: italic;
}

.advanced-filters {
  border-left: 2px solid #21262d;
  padding-left: 10px;
  margin-left: 4px;
  margin-bottom: 6px;
}

.filter-impact {
  font-size: 0.7rem;
  font-family: 'JetBrains Mono', monospace;
  padding: 1px 4px;
  border-radius: 3px;
}
.filter-impact.neg { background: #3a1b1b; color: #f85149; }

.filter-label {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #aaa;
  margin-bottom: 6px;
  min-height: 28px;
  align-items: center;
  cursor: pointer;
}

.filter-label input {
  width: 16px;
  height: 16px;
}

/* ---- Walk-forward inputs ---- */

.wf-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.wf-label {
  font-size: 12px;
  color: #aaa;
  width: 80px;
}

.num-input {
  width: 70px;
  padding: 4px 6px;
  background: #222;
  color: #e0e0e0;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 12px;
  min-height: 28px;
}

/* ---- Action buttons ---- */

.action-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.btn-run {
  flex: 1;
  padding: 10px;
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 13px;
  min-height: 44px;
}

.btn-run:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-tpe {
  background: #9c27b0;
}

.run-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.btn-cancel {
  padding: 10px 14px;
  background: #b3261e;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  min-height: 44px;
  flex: 0 0 auto;
}

.btn-cancel:hover {
  background: #d32f2f;
}

.run-preview {
  font-size: 0.75rem;
  color: #8b949e;
  margin-top: 6px;
  text-align: center;
  font-family: 'SF Mono', Consolas, monospace;
}
.stack-preview { font-size: 10px; opacity: 0.85; }
.stack-warn { color: #e3b341; font-weight: 600; opacity: 1; }

.btn-schedule {
  padding: 10px 14px;
  background: #333;
  color: #ccc;
  border: 1px solid #555;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  min-height: 44px;
}

.btn-action {
  padding: 6px 12px;
  background: #222;
  color: #4caf50;
  border: 1px solid #333;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  min-height: 28px;
}

.btn-delete {
  color: #f44336;
  min-width: 36px;
}
.btn-delete:disabled {
  opacity: 0.3;
}


.saved-configs-list {
  margin-top: 8px;
}
.saved-config-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border-bottom: 1px solid #2a2a2a;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}
.saved-config-row:hover {
  background: #222;
}
.saved-config-row.active-config {
  border-left: 3px solid #4caf50;
  padding-left: 5px;
}
.config-info {
  flex: 1;
  min-width: 0;
}
.config-name {
  color: #ccc;
  font-weight: 500;
  margin-right: 6px;
}
.active-badge {
  font-size: 9px;
  color: #4caf50;
  background: #1a2a1a;
  padding: 1px 5px;
  border-radius: 3px;
  margin-right: 6px;
  text-transform: uppercase;
}
.config-detail {
  color: #666;
  font-size: 10px;
  font-family: monospace;
}
.btn-micro {
  padding: 3px 8px;
  border: 1px solid #444;
  background: #222;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  min-height: 24px;
}
.btn-micro-active {
  color: #4caf50;
}
.btn-micro-active:hover {
  background: #1a2a1a;
  border-color: #4caf50;
}
.btn-micro-delete {
  color: #f44336;
}
.btn-micro-delete:hover {
  background: #2a1a1a;
  border-color: #f44336;
}

.env-badge {
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 3px;
  text-transform: uppercase;
  margin-left: 6px;
  vertical-align: middle;
  font-weight: bold;
}
.env-debug { background: #1a2a3a; color: #64b5f6; border: 1px solid #1565c0; }
.env-optimization { background: #2a2a1a; color: #ff9800; border: 1px solid #e65100; }
.env-prod { background: #1a2a1a; color: #4caf50; border: 1px solid #2e7d32; }

.best-fit-notice {
  padding: 8px 10px;
  background: #1a2a1a;
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  font-size: 11px;
  color: #4caf50;
  margin-bottom: 10px;
}

.schedule-row {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.error-box {
  margin-top: 8px;
  padding: 8px;
  background: #3a1b1b;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 11px;
}

/* ---- Progress bar ---- */

.progress-box {
  margin-top: 10px;
  padding: 10px;
  background: #1a2a1a;
  border: 1px solid #2a4a2a;
  border-radius: 4px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #4caf50;
  margin-bottom: 4px;
}

.progress-track {
  background: #333;
  border-radius: 2px;
  height: 6px;
  overflow: hidden;
}

.progress-fill {
  background: #4caf50;
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s;
}

.progress-eta {
  font-size: 10px;
  color: #666;
  margin-top: 4px;
  text-align: right;
}

/* ---- Results Panel ---- */

.results-panel {
  flex: 1;
  overflow: auto;
}

.no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #555;
  font-style: italic;
  font-size: 14px;
}

/* ---- View toggle ---- */

.live-applied-banner {
  background: linear-gradient(135deg, #1a3a1a, #0a1a0a);
  border: 1px solid #4caf50;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 0.8rem;
  color: #4caf50;
  text-align: center;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.recent-runs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  flex-wrap: wrap;
}

.recent-runs select {
  max-width: 100%;
}

.loaded-row-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: #1c2a3a;
  border-left: 3px solid #58a6ff;
  color: #c9d1d9;
  font-size: 0.85rem;
}

.results-action-bar {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  background: #1a1f25;
  border-bottom: 1px solid #2a2f35;
  position: sticky;
  top: 0;
  z-index: 10;
}

.strategy-banner {
  background: linear-gradient(135deg, #1a2a1a, #0a0a0a);
  border: 1px solid #2a4a2a;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4caf50;
  text-align: center;
  letter-spacing: 0.5px;
}

.run-summary { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; padding: 10px 12px; margin-bottom: 12px; font-family: 'JetBrains Mono', monospace; }
.run-summary-top { font-size: 13px; color: #e0e0e0; }
.run-type { font-weight: 600; color: #4caf50; }
.run-preset { color: #888; font-weight: normal; }
.run-summary-meta { font-size: 11px; color: #888; margin-top: 4px; }
.run-summary-winner { font-size: 11px; color: #ccc; margin-top: 4px; }

/* ---- KPI cards ---- */

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.kpi {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 10px;
  text-align: center;
}

.kpi-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 2px;
}

.kpi-value {
  font-size: 20px;
  font-weight: bold;
  color: #e0e0e0;
}

.kpi-value.positive {
  color: #4caf50;
}

.kpi-value.negative {
  color: #f44336;
}

/* ---- Stats row ---- */

.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 11px;
  color: #aaa;
}

.stat-label {
  color: #666;
}

/* ---- Equity placeholder ---- */

.equity-chart {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}
.equity-svg {
  width: 100%;
  height: auto;
  min-height: 120px;
  max-height: 200px;
}

.equity-placeholder {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
  color: #555;
  font-size: 12px;
  font-style: italic;
  margin-bottom: 12px;
}

/* ---- Comparison bar ---- */

.comparison-bar {
  display: flex;
  gap: 16px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}

.comparison-side {
  flex: 1;
}

.comparison-label {
  font-size: 10px;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.comparison-metrics {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #e0e0e0;
}

.decay-row {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  font-size: 10px;
  align-items: center;
}

.decay-label {
  color: #666;
}

.decay-warn {
  color: #ff9800;
}

/* ---- Fold table ---- */

.fold-section {
  margin-bottom: 12px;
}

.opt-summary {
  font-size: 11px;
  color: #888;
  padding: 6px 0;
  font-family: monospace;
}

.fold-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.fold-table th {
  text-align: left;
  padding: 5px;
  color: #666;
  border-bottom: 1px solid #444;
}

.fold-table td {
  padding: 5px;
  border-bottom: 1px solid #252525;
  color: #e0e0e0;
}

/* ---- Shared value classes ---- */

.positive {
  color: #4caf50;
}

.negative {
  color: #f44336;
}

/* ---- Responsive ---- */

@media (max-width: 768px) {
  .backtest-panel {
    flex-direction: column;
  }

  .controls-panel {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #333;
    padding-right: 0;
    padding-bottom: 12px;
  }

  .controls-panel.collapsed .controls-body {
    display: none;
  }

  .config-summary {
    display: inline;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 769px) {
  .config-summary {
    display: none;
  }

  .controls-panel .controls-body {
    display: block !important;
  }
}

/* ---- Hedging Strategy ---- */

.hedge-params {
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.hedge-param-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.param-label {
  font-size: 0.75rem;
  color: #888;
  min-width: 100px;
}

.param-select, .param-input {
  background: #111;
  border: 1px solid #333;
  color: #ccc;
  padding: 4px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
  flex: 1;
  max-width: 140px;
}

.param-unit {
  font-size: 0.7rem;
  color: #666;
  min-width: 30px;
}

.advanced-toggle { cursor: pointer; user-select: none; font-size: 12px; color: #888; padding: 4px 0; font-weight: 600; }
.advanced-toggle:hover { color: #ccc; }
.advanced-chevron { display: inline-block; width: 12px; }
.advanced-body { margin-top: 8px; padding-left: 8px; border-left: 2px solid #333; }
.subsection { margin-bottom: 16px; }
.subsection:last-child { margin-bottom: 0; }
.subsection-header { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; margin-bottom: 6px; }
.param-note { font-size: 11px; color: #888; font-style: italic; margin-bottom: 4px; }

.section-toggle {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.active-badge { color: #3fb950; font-size: 0.85rem; font-weight: 600; }

/* ---- CPCV ---- */
.cpcv-results {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.cpcv-summary, .cpcv-distribution, .cpcv-paths, .cpcv-fan-chart {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 0.75rem;
}
.fan-legend {
  display: flex; gap: 16px; margin-top: 6px; font-size: 11px; color: #8b949e;
}
.fan-legend-item { display: flex; align-items: center; gap: 4px; }
.fan-legend-item .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.fan-legend-item .swatch { width: 14px; height: 10px; border-radius: 2px; display: inline-block; }

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.summary-grid .label {
  color: #888;
  margin-right: 0.3rem;
}

.metrics-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
}

.metrics-table th, .metrics-table td {
  padding: 0.4rem 0.6rem;
  text-align: right;
  border-bottom: 1px solid #333;
}

.metrics-table th:first-child, .metrics-table td:first-child {
  text-align: left;
}

.metrics-table th {
  color: #888;
  font-weight: normal;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.4rem;
}

.chart-svg {
  width: 100%;
  height: auto;
}

.cpcv-results h3, .cpcv-results h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: #aaa;
  font-weight: normal;
}

/* ---- TPE Full Optimize results ---- */
.tpe-groups { margin-top: 8px; }
.tpe-group-row { cursor: pointer; }
.tpe-group-row:hover { background: #161b22; }
.tpe-winner { background: #0d1117; border-left: 3px solid #3fb950; }

.mit-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
}
.mit-table th {
  text-align: left;
  padding: 5px 6px;
  color: #666;
  border-bottom: 1px solid #333;
  font-weight: normal;
}
.mit-table td {
  padding: 5px 6px;
  color: #e0e0e0;
}
.mit-table tbody tr { border-bottom: 1px solid #161b22; }
.pill-dim { color: #666; }

/* ---- Portfolio pill (Task 4) ---- */
.portfolio-pill {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  margin-left: 0.5rem;
  vertical-align: middle;
}
.pill-bga { background: #2a2a2a; color: #888; }
.pill-jh { background: #4a3000; color: #f0c060; }
.pill-gold { background: #3a2800; color: #d4a800; }
</style>
